import type { Translations } from './en';

export const pl: Translations = {
  app: {
    name: 'Periscope',
    tagline: 'Zarządzanie firmą w jednym miejscu',
  },

  common: {
    loading: 'Wczytywanie...',
    error: 'Wystąpił błąd',
    newVersionAvailable: 'Dostępna nowa wersja aplikacji (v{version})',
    refreshNow: 'Odśwież',
    save: 'Zapisz',
    cancel: 'Anuluj',
    delete: 'Usuń',
    edit: 'Edytuj',
    add: 'Dodaj',
    search: 'Szukaj',
    filter: 'Filtruj',
    apply: 'Zastosuj',
    clear: 'Wyczyść',
    actions: 'Akcje',
    confirm: 'Potwierdź',
    yes: 'Tak',
    no: 'Nie',
    all: 'Wszystkie',
    none: 'Brak',
    export: 'Eksportuj',
    exportTo: 'Eksportuj do',
    downloadFailed: 'Nie udało się pobrać pliku. Spróbuj ponownie.',
    undo: 'Cofnij',
  },

  // CRM — Spec: docs/superpowers/specs/2026-09-09-crm-module-design.md
  crm: {
    title: 'CRM',
    tabs: { leads: 'Leady', accounts: 'Klienci', deals: 'Szanse' },
    accountStatus: {
      lead: 'Lead',
      prospect: 'Potencjalny',
      customer: 'Klient',
      inactive: 'Nieaktywny',
    },
    leadStatus: {
      new: 'Nowy',
      assigned: 'Przypisany',
      converted: 'Przekonwertowany',
      spam: 'Spam',
      rejected: 'Odrzucony',
    },
    leadRejectReason: {
      spam: 'Spam',
      not_interested: 'Niezainteresowany',
      no_contact: 'Brak kontaktu',
      out_of_scope: 'Nie nasz zakres',
      duplicate: 'Duplikat',
      other: 'Inny',
    },
    activityType: {
      note: 'Notatka',
      call: 'Rozmowa',
      meeting: 'Spotkanie',
      email_out: 'Mail wysłany',
      email_in: 'Mail otrzymany',
      stage_change: 'Zmiana etapu',
      lead_received: 'Lead przyjęty',
      lead_converted: 'Lead przekonwertowany',
      deal_created: 'Szansa utworzona',
      task_done: 'Zadanie zamknięte',
      file: 'Plik',
    },
    leads: {
      title: 'Kolejka leadów',
      searchPlaceholder: 'Szukaj: e-mail, telefon, firma, treść…',
      allStatuses: 'Wszystkie statusy',
      newLead: 'Nowy lead',
      empty: 'Brak leadów spełniających filtry',
      selected: 'Zaznaczono: {count}',
      selectAllResults: 'Zaznacz wszystkie wyniki ({count})',
      clearSelection: 'Wyczyść zaznaczenie',
      assignOwner: 'Przypisz opiekuna',
      convert: 'Konwertuj',
      hide: 'Ukryj',
      hidden: 'Lead ukryty',
      hiddenMany: 'Ukryto: {count}',
      deleted: 'Lead usunięty',
      deletedMany: 'Usunięto: {count}',
      activeStatuses: 'Aktywne',
      skipped: 'Pominięto: {count}',
      reasonLabel: 'Powód',
      restore: 'Przywróć',
      hideDialog: {
        title: 'Ukryj lead',
        titleMany: 'Ukryj {count} leadów',
        description: 'Lead zniknie z widoku aktywnych; można go przywrócić.',
        reason: 'Powód',
        note: 'Notatka (opcjonalnie)',
        notePlaceholder: 'np. klient wybrał konkurencję',
        submit: 'Ukryj',
      },
      deleteDialog: {
        title: 'Usunąć lead?',
        titleMany: 'Usunąć {count} leadów?',
        description: 'Operacja jest nieodwracalna. Przekonwertowanych leadów nie można usunąć.',
      },
      columns: {
        received: 'Wpłynął',
        source: 'Źródło',
        contact: 'Kontakt',
        company: 'Firma',
        message: 'Wiadomość',
        status: 'Status',
        account: 'Klient',
      },
      detail: {
        title: 'Szczegóły leada',
        rawPayload: 'Surowy payload',
        utm: 'UTM',
        sourceUrl: 'Adres formularza',
        externalId: 'Identyfikator zewnętrzny',
        consent: 'Zgoda marketingowa',
        timeline: 'Historia',
      },
      convertDialog: {
        title: 'Konwersja leada',
        existingAccount: 'Istniejący klient',
        newAccount: 'Nowy klient',
        accountName: 'Nazwa klienta',
        createContact: 'Utwórz kontakt z danych leada',
        createDeal: 'Utwórz szansę sprzedażową',
        dealName: 'Nazwa szansy',
        dealValue: 'Wartość netto',
        submit: 'Konwertuj',
      },
      created: 'Lead dodany',
      converted: 'Lead przekonwertowany',
      restored: 'Lead przywrócony',
      restoredMany: 'Przywrócono: {count}',
      assigned: 'Zaktualizowano {count} leadów',
    },
    accounts: {
      title: 'Klienci',
      searchPlaceholder: 'Szukaj: nazwa, NIP, miasto, e-mail…',
      allStatuses: 'Wszystkie statusy',
      newAccount: 'Nowy klient',
      empty: 'Brak klientów spełniających filtry',
      columns: {
        name: 'Nazwa',
        nip: 'NIP',
        status: 'Status',
        city: 'Miasto',
        owner: 'Opiekun',
        openDeals: 'Otwarte szanse',
        openValue: 'Wartość otwarta',
        lastActivity: 'Ostatnia aktywność',
      },
      form: {
        name: 'Nazwa',
        legalName: 'Pełna nazwa',
        nip: 'NIP',
        vatId: 'VAT UE',
        fetchRegistry: 'Pobierz dane',
        website: 'Strona WWW',
        emailDomains: 'Domeny e-mail',
        emailDomainsHint: 'Po przecinku. Domeny publiczne (gmail.com itp.) są pomijane.',
        email: 'E-mail',
        phone: 'Telefon',
        street: 'Ulica',
        postalCode: 'Kod pocztowy',
        city: 'Miasto',
        country: 'Kraj',
        industry: 'Branża',
        status: 'Status',
        owner: 'Opiekun',
        notes: 'Notatki',
      },
      detail: {
        deals: 'Szanse',
        timeline: 'Historia',
        tasks: 'Zadania',
        addNote: 'Dodaj notatkę',
        noteePlaceholder: 'Co się wydarzyło?',
        noDeals: 'Brak szans',
        noActivity: 'Brak historii',
      },
      tabs: {
        overview: 'Przegląd',
        documents: 'Dokumenty',
      },
      documents: {
        noContractorHint: 'Dokumenty klienta trzymamy na karcie kontrahenta w kartotece księgowej. Utwórz kontrahenta, aby zacząć dodawać dokumenty.',
        createAndUpload: 'Utwórz kontrahenta i dodaj dokument',
      },
      saved: 'Zapisano klienta',
      nipConflict: 'Klient z tym NIP-em już istnieje',
      contractor: {
        title: 'Kontrahent',
        none: 'Ten klient nie jest jeszcze powiązany z żadnym kontrahentem w kartotece księgowej.',
        add: 'Dodaj do kontrahentów',
        unlink: 'Odłącz',
        unlinkConfirmTitle: 'Odłączyć kontrahenta?',
        unlinkConfirmBody: 'Klient CRM przestanie być powiązany z tym kontrahentem. Sam kontrahent i jego faktury zostają bez zmian.',
        open: 'Otwórz kartę kontrahenta',
        created: 'Utworzono kontrahenta',
        linkedExisting: 'Powiązano z istniejącym kontrahentem',
        linked: 'Powiązano kontrahenta',
        unlinked: 'Odłączono kontrahenta',
        alreadyLinked: 'Ten klient już ma powiązanego kontrahenta',
        match: {
          open: 'Dopasuj',
          title: 'Dopasuj kontrahenta',
          empty: 'Nie znaleziono podobnych kontrahentów. Możesz dodać nowego.',
          exact: 'Ten sam NIP',
          name: 'Podobna nazwa',
          link: 'Powiąż',
        },
        loadFailed: 'Nie udało się wczytać kandydatów',
        linkedTo: 'Powiązany już z klientem „{name}"',
      },
    },
    contacts: {
      title: 'Kontakty',
      add: 'Dodaj kontakt',
      edit: 'Edytuj kontakt',
      empty: 'Brak kontaktów',
      primary: 'Główny',
      saved: 'Kontakt zapisany',
      form: {
        firstName: 'Imię',
        lastName: 'Nazwisko',
        jobTitle: 'Stanowisko',
        email: 'E-mail',
        phone: 'Telefon',
        isPrimary: 'Kontakt główny',
        notes: 'Notatki',
        submit: 'Zapisz',
      },
    },
    deals: {
      empty: 'Brak szans',
      lostReason: 'Powód przegranej',
      lostReasonRequired: 'Przegrana wymaga podania powodu',
      // Board (drag & drop) + deal sheet — spec 2026-09-10
      board: {
        searchPlaceholder: 'Szukaj: nazwa, klient…',
        summary: 'Otwarte: {count}',
        weighted: 'ważone {sum}',
        otherCurrency: '(+{count} w {currency})',
        mine: 'Moje',
        overdue: 'Zaległe',
        showClosed: 'Zamknięte',
        last30Days: 'ostatnie {days} dni',
        lostSum: 'stracone {sum}',
        staleCount: '{count} bez aktywności',
        overdueCount: '{count} po terminie',
        inactiveStage: 'Etap nieaktywny',
        dropHere: 'Przeciągnij tutaj',
        dropWon: 'Upuść, by oznaczyć wygraną',
        dropLost: 'Upuść, by oznaczyć przegraną',
        movedTo: 'Przeniesiono do: {stage}',
        undoNote: 'Cofnięcie zmiany etapu',
        moveFailed: 'Nie udało się zmienić etapu — przywrócono poprzedni',
        won: 'Wygrana! {name}',
        lost: 'Oznaczono jako przegraną',
        resumed: "Szansa wróciła do pipeline'u",
        emptyTitle: 'Brak szans w lejku',
        emptyHint: 'Szanse powstają z konwersji leada.',
        goToLeads: 'Przejdź do leadów',
        noMatch: 'Nic nie pasuje do filtrów',
        refresh: 'Odśwież',
        retry: 'Spróbuj ponownie',
        shortcuts: {
          title: 'Skróty klawiszowe',
          open: 'Otwórz',
          lift: 'Podnieś kartę',
          move: 'Zmień etap (strzałki)',
          drop: 'Upuść / anuluj',
          nextStage: 'Następny etap',
          prevStage: 'Poprzedni etap',
          won: 'Wygrana',
          lost: 'Przegrana',
          search: 'Szukaj',
        },
      },
      card: {
        noOwner: 'bez opiekuna',
        inStage: '{days} dni w etapie',
        inStageOne: '1 dzień w etapie',
        noContact: 'brak kontaktu {days} dni',
        tooltip: 'Ostatnia aktywność: {activity} · w etapie od {since}',
        noActivityYet: 'brak',
      },
      relative: {
        today: 'dziś',
        tomorrow: 'jutro',
        yesterday: 'wczoraj',
        inDays: 'za {days} dni',
        daysAgo: '{days} dni temu',
        overdueDays: '{days} dni po terminie',
        overdueOne: '1 dzień po terminie',
      },
      dnd: {
        start:
          'Podniesiono szansę „{name}” z etapu {stage}. Strzałki w lewo i w prawo zmieniają etap, spacja upuszcza, Escape anuluje.',
        over: '„{name}” nad etapem {stage}',
        end: 'Przeniesiono „{name}” do etapu {stage}',
        cancel: 'Anulowano przenoszenie',
        instructions:
          'Naciśnij spację, aby podnieść szansę. Strzałki w lewo i w prawo zmieniają etap, spacja upuszcza, Escape anuluje.',
      },
      close: {
        lostTitle: 'Oznacz jako przegraną',
        lostConfirm: 'Oznacz jako przegraną',
        reasons: {
          price: 'Cena',
          competition: 'Konkurencja',
          noBudget: 'Brak budżetu',
          noDecision: 'Brak decyzji',
          noContact: 'Brak kontaktu',
          other: 'Inne',
        },
        wonTitle: 'Oznacz jako wygraną',
        wonBody: 'Klient {account} otrzyma status „Klient”. Wartość: {value}.',
        wonNote: 'Notatka (opcjonalnie)',
        wonConfirm: 'Potwierdź wygraną',
        wonAccountStays: 'Status klienta pozostaje bez zmian',
      },
      detail: {
        overline: 'Szansa',
        notFound: 'Szansa nie istnieje lub brak dostępu',
        openAccount: 'Otwórz kartę klienta',
        copyLink: 'Kopiuj link',
        linkCopied: 'Skopiowano link',
        createdAgo: 'utworzona {date} ({ago})',
        fromLead: 'z leada',
        unsavedNote: 'Masz niezapisany wpis — Esc jeszcze raz zamknie',
        nameRequired: 'Nazwa nie może być pusta',
        tabs: { history: 'Historia', data: 'Dane' },
      },
      health: {
        fresh: 'Aktywna · {ago}',
        freshNoActivity: 'Nowa · bez aktywności',
        stale: 'Bez aktywności · {days} dni',
        staleOne: 'Bez aktywności · 1 dzień',
        overdue: 'Po terminie · {days} dni',
        overdueOne: 'Po terminie · 1 dzień',
      },
      stepper: {
        moveTo: 'Przenieś do: {stage} ({p} %)',
        inStageSince: 'W etapie {stage} od {date} ({days} dni) · prawdopodobieństwo {p} %',
        inStageSinceOne: 'W etapie {stage} od {date} (1 dzień) · prawdopodobieństwo {p} %',
        won: 'Wygrana',
        lost: 'Przegrana',
        wonBanner: 'Wygrana {date} · {value}',
        wonCustomer: 'Klient ma status „Klient”',
        lostBanner: 'Przegrana {date}',
        reason: 'Powód: {reason}',
        enteredBy: 'wpisał {author}',
        resume: "Przywróć do pipeline'u",
        resumeConfirm: 'Szansa wróci do etapu {stage}. Powód przegranej zostanie usunięty.',
      },
      facts: {
        value: 'Wartość netto',
        probability: 'Prawdopodobieństwo',
        weighted: 'Wartość ważona',
        expectedClose: 'Przewidywane zamknięcie',
        owner: 'Opiekun',
        account: 'Klient',
        primaryContact: 'Kontakt główny',
        project: 'Projekt',
        notes: 'Notatki',
        createdAt: 'Utworzono',
        updatedAt: 'Ostatnia zmiana',
        stageDefault: 'domyślnie z etapu: {p} %',
        stageOverrides: 'Zmiana etapu przywraca wartość z etapu',
        pastDue: '(po terminie)',
        assignMe: 'Przypisz mnie',
        noOwner: '— bez opiekuna —',
        noContact: '— brak —',
        searchUser: 'Szukaj po nazwisku…',
        quick: { week: '+1 tydz.', month: '+1 mies.', quarter: 'koniec kwartału', clear: 'Wyczyść' },
        notesPlaceholder: 'Kontekst, ustalenia, wymagania…',
        savedFlash: 'Zapisano',
      },
      composer: {
        save: 'Zapisz',
        subject: 'Temat',
        time: 'Godzina',
        today: 'dziś {time}',
        yesterday: 'wczoraj',
        minusHour: '-1 h',
        call: 'Zadzwoń',
        placeholder: {
          note: 'Co się wydarzyło?',
          call: 'Z kim i co ustaliliście?',
          meeting: 'Kto był, ustalenia, następne kroki',
          email_out: 'Temat i streszczenie',
          email_in: 'Temat i streszczenie',
        },
      },
      timeline: {
        all: 'Wszystko',
        entries: 'Wpisy',
        stageChanges: 'Zmiany etapu',
        today: 'Dziś',
        yesterday: 'Wczoraj',
        more: 'więcej',
        empty: 'Brak wpisów — zapisz pierwszą rozmowę.',
        created: 'Utworzono {date}',
        fullAccountHistory: 'Pełna historia klienta →',
        reason: 'Powód:',
      },
      nextStep: {
        title: 'Co dalej?',
        overdue: 'Termin zamknięcia minął {days} dni temu',
        overdueYesterday: 'Termin zamknięcia minął wczoraj',
        noContact: 'Brak kontaktu od {days} dni — zadzwoń do {contact}',
        noContactGeneric: 'Brak kontaktu od {days} dni',
        noValue: 'Uzupełnij wartość — bez niej lejek nie liczy prognozy',
        noDate: 'Ustal przewidywane zamknięcie',
        onTrack: 'Wszystko na czasie — następny etap: {stage}',
        actions: {
          moveDate: 'Przesuń termin',
          logCall: 'Zapisz rozmowę',
          enterValue: 'Wpisz wartość',
          setDate: 'Ustaw datę',
          moveNext: 'Przenieś →',
          call: 'Zadzwoń',
        },
      },
    },
  },

  navigation: {
    tags: 'Tagi',
    companies: 'Firmy',
    unassigned: 'Nieprzypisane',
    invoices: 'FV Koszty',
    sales: 'Sprzedaż',
    crm: 'CRM',
    fiscal: 'Fiskalizacja',
    projects: 'Projekty',
    contractors: 'Kontrahenci',
    magazyn: 'Magazyn',
    dashboard: 'Panel',
    mileage: 'Ewidencja przebiegu',
    employees: 'Pracownicy',
    hr: 'HR',
    documents: 'Dokumenty',
    administration: 'Administracja',
    users: 'Użytkownicy',
    apiApps: 'Aplikacje API',
  },

  invoices: {
    title: 'FV Koszty',
    titleWithCompany: 'FV Koszty: {companyName}',
    subtitle: 'Przeglądaj i zarządzaj fakturami dla tej firmy',
    searchPlaceholder: 'Szukaj po numerze faktury, numerze KSeF, nazwie kontrahenta lub kwocie',
    noInvoices: 'Nie znaleziono faktur',
    noInvoicesDescription: 'Brak faktur pasujących do wybranych filtrów',

    columns: {
      index: '#',
      pdf: 'PDF',
      source: 'Źródło',
      contractor: 'Kontrahent',
      invoiceNumber: 'Numer faktury',
      issueDate: 'Data wystawienia',
      receivedAt: 'Otrzymano',
      status: 'Status',
      exportedAt: 'Wyeksportowano',
      tags: 'Tagi',
      exportStatus: 'Eksport',
      amount: 'Kwota',
      actions: 'Akcje',
      detectedNip: 'Wykryty NIP',
      notes: 'Opisy',
      project: 'Projekt',
    },

    source: {
      email: 'E-mail',
      upload: 'Upload',
      ksef: 'KSeF',
      import: 'Import (archiwum)',
    },

    filters: {
      mpp: {
        all: 'Wszystkie',
        mppOnly: 'Tylko MPP',
        withoutMpp: 'Bez MPP',
      },
      dateType: {
        label: 'Typ daty',
        issueDate: 'Data wystawienia',
        saleDate: 'Data sprzedaży',
        paymentDate: 'Termin płatności',
        createdAt: 'Data utworzenia',
      },
      dateFrom: 'Od daty',
      dateTo: 'Do daty',
      status: {
        all: 'Wszystkie',
        paid: 'Zapłacone',
        unpaid: 'Niezapłacone',
        exported: 'Wyeksportowane',
      },
      reimbursement: {
        label: 'Zwroty',
        all: 'Wszystkie',
        with: 'Ze zwrotem',
        pending_approval: 'Czeka na akceptację',
        approved: 'Do eksportu bankowego',
        exported: 'Przelew zlecony',
        without: 'Bez zwrotu',
      },
      applyFilters: 'Zastosuj filtry',
      clearAll: 'Wyczyść filtry',
      refresh: 'Odśwież',
      noNotes: 'Bez opisu',
      projectAll: 'Wszystkie',
    },

    detail: {
      loadError: 'Nie udało się wczytać faktury',
      saveSuccess: 'Faktura została zapisana',
      saveError: 'Nie udało się zapisać faktury',
      vatRecheckSuccess: 'Status VAT zaktualizowany',
      vatRecheckError: 'Nie udało się zweryfikować statusu VAT',
      downloadPdfError: 'Nie udało się pobrać PDF',
      downloadOriginalError: 'Nie udało się pobrać oryginału',
      invoiceNumberPlaceholder: 'Wpisz numer faktury',
      sellerNamePlaceholder: 'Nazwa sprzedawcy',
      taxIdPlaceholder: 'NIP',
      cityPlaceholder: 'Miejscowość',
      discardConfirmTitle: 'Odrzucić niezapisane zmiany?',
      discardConfirmMessage: 'Masz niezapisane zmiany w tej fakturze. Jeśli teraz wyjdziesz, zostaną utracone.',
      discardConfirmAction: 'Odrzuć zmiany',
      tabPdf: 'PDF',
      tabLineItems: 'Pozycje',
    },

    status: {
      paid: 'Zapłacona',
      unpaid: 'Niezapłacona',
      exported: 'Wyeksportowana',
    },

    actions: {
      open: 'Otwórz',
      viewPdf: 'Zobacz PDF',
      editTags: 'Edytuj tagi',
      changeStatus: 'Zmień status',
      moreActions: 'Więcej akcji',
      delete: 'Usuń',
    },

    bulk: {
      selected: 'Zaznaczono: {count}',
      addTags: 'Dodaj tagi',
      removeTags: 'Usuń tagi',
      markAsPaid: 'Oznacz jako zapłacone',
      markAsUnpaid: 'Oznacz jako niezapłacone',
      markAsExported: 'Oznacz jako wyeksportowane',
      addTagsModalTitle: 'Dodaj tagi do faktur',
      addTagsDescription: 'Wybierz tagi do dodania do {count} faktur(y):',
      statusSuccess: 'Zaktualizowano status {count} faktur(y) na {status}',
      statusError: 'Nie udało się zaktualizować statusu faktur',
      tagsSuccess: 'Dodano tagi do {count} faktur(y)',
      tagsError: 'Nie udało się dodać tagów',
      assignToCompany: 'Przypisz do firmy',
      assignModalTitle: 'Przypisz faktury do firmy',
      assignModalDescription: 'Wybierz firmę, do której przypisać {count} faktur(y):',
      assignSuccess: 'Przypisano {count} faktur(y)',
      assignError: 'Nie udało się przypisać faktur',
      selectCompany: 'Wybierz firmę',
      assignToProject: 'Przypisz do projektu',
      projectModalTitle: 'Przypisz faktury do projektu',
      projectModalDescription: 'Wybierz projekt, do którego przypisać {count} faktur(y):',
      selectProject: 'Wybierz projekt',
      noProject: 'Brak projektu',
      projectSuccess: 'Zaktualizowano projekt {count} faktur(y)',
      projectError: 'Nie udało się przypisać projektu',
      delete: 'Usuń',
      deleteConfirmTitle: 'Usuń faktury',
      deleteSuccess: 'Usunięto {count} faktur(y)',
      deleteError: 'Nie udało się usunąć faktur',
    },

    assignProject: {
      title: 'Przypisz fakturę do projektu',
      description: 'Wybierz projekt dla faktury {invoiceNumber}:',
      success: 'Zaktualizowano przypisanie do projektu',
      error: 'Nie udało się przypisać projektu',
    },

    assign: {
      sectionTitle: 'Ta faktura nie jest przypisana',
      button: 'Przypisz',
      success: 'Faktura została przypisana',
      error: 'Nie udało się przypisać faktury',
      changeCompany: 'Zmień firmę',
      changeCompanyTitle: 'Przenieś fakturę do innej firmy',
    },

    delete: {
      confirmTitle: 'Usuń fakturę',
      confirmMessage: 'Czy na pewno chcesz trwale usunąć fakturę {invoiceNumber}? Tej operacji nie można cofnąć.',
      success: 'Faktura została usunięta',
      error: 'Nie udało się usunąć faktury',
    },

    bulkDeleteConfirm: 'Usunąć {count} faktur wraz z plikami? Tej operacji nie można cofnąć.',

    export: {
      selectToExport: 'Wybierz faktury do eksportu',
      pdfZipLoading: 'Generowanie ZIP z plikami PDF...',
      pdfZipSuccess: 'Pobrano ZIP z plikami PDF',
      pdfZipError: 'Nie udało się wygenerować ZIP z plikami PDF',
      ready: 'Gotowa do eksportu mBank',
      issues: {
        no_bank_account: 'Brak rachunku bankowego (IBAN)',
        invalid_iban: 'Nieprawidłowy format IBAN',
        no_amount: 'Brak kwoty lub kwota zerowa',
        no_contractor_name: 'Brak nazwy kontrahenta',
        mpp_no_nip: 'MPP: brak NIP kontrahenta',
        mpp_no_vat: 'MPP: brak kwoty VAT',
        non_transfer: 'Forma płatności inna niż przelew',
        already_paid: 'Już zapłacona',
        already_exported: 'Już wyeksportowana',
        invalid_date: 'Nieprawidłowa data płatności',
        swift_needs_bic: 'Przelew SWIFT wymaga kodu BIC — uzupełnij poniżej',
        missing_creditor_city: 'Brak miasta kontrahenta',
        missing_creditor_country: 'Brak kraju kontrahenta (międzynar.)',
        sepa_not_eur: 'SEPA wymaga waluty EUR',
        mpp_intl_conflict: 'MPP nieobsługiwane dla przelewów międzynarodowych',
        non_iban_account: 'Konto nie-IBAN (tylko SWIFT)',
        not_vat_payer: 'Kontrahent nie jest na białej liście VAT (bez /WL!)',
        invalid_country_format: 'Kraj kontrahenta nie jest kodem ISO (np. "Canada" zamiast "CA")',
        no_debtor_account_for_currency: 'Firma nie ma rachunku w walucie faktury',
      },
    },

    upload: {
      button: 'Prześlij fakturę',
      tooltip: 'Prześlij fakturę w formacie PDF',
      modalTitle: 'Prześlij fakturę',
      descriptionPlaceholder: 'Opcjonalny opis...',
      success: 'Faktura została przesłana',
      notInvoice: 'Dokument przesłany, ale nie rozpoznany jako faktura',
      error: 'Nie udało się przesłać faktury',
    },

    // Płatność łączona — jedna transakcja bankowa/kartą pokrywająca kilka FV
    reimbursement: {
      pending_approval: 'Zwrot do pracownika — czeka na akceptację',
      approved: 'Zwrot do pracownika — do eksportu bankowego',
      exported: 'Zwrot do pracownika — przelew zlecony',
    },
    combinedPayment: {
      approxNotConfirmable: 'Brak dokładnego dopasowania — pokazane tylko poglądowo, nie można powiązać.',
      button: 'Dopasuj płatność',
      badgeTooltip: 'Płatność łączona — opłacona jedną transakcją razem z innymi fakturami',
      dialogTitle: 'Dopasuj płatność łączoną',
      dialogDescription:
        'Podaj datę i kwotę transakcji z wyciągu, a system znajdzie opłacone faktury kosztowe, które sumują się dokładnie do tej kwoty (np. zakupy w Allegro od kilku sprzedawców opłacone jedną płatnością — FV przychodzą już jako opłacone). Statusy faktur nie są zmieniane.',
      transactionDate: 'Data transakcji (z wyciągu)',
      amount: 'Kwota (PLN)',
      searchButton: 'Szukaj dopasowań',
      searchError: 'Nie udało się wyszukać dopasowań',
      noMatches:
        'Brak kombinacji faktur sumujących się do tej kwoty. Przeszukano {count} opłaconych (i niepowiązanych) faktur z okresu {from} – {to}.',
      approxHeader:
        'Brak dokładnych dopasowań — poniżej najbliższe kombinacje. Sprawdź różnicę przed zaksięgowaniem!',
      comboLabel: 'Kombinacja: {count} FV',
      difference: 'Różnica',
      copy: 'Kopiuj',
      copied: 'Skopiowano do schowka',
      copyError: 'Nie udało się skopiować',
      sumLabel: 'Suma',
      noteLabel: 'Notatka',
      notePlaceholder: 'np. Allegro, karta służbowa...',
      accept: 'Powiąż jako płatność łączoną',
      acceptSuccess: 'Powiązano {count} FV z płatnością {amount}',
      acceptError: 'Nie udało się powiązać faktur',
      truncated: 'Kombinacji może być więcej — pokazano najbardziej prawdopodobne.',
      cardTitle: 'Płatność łączona',
      cardTransaction: 'Transakcja z {date} na kwotę {amount}',
      cardCreatedBy: 'Powiązał(a): {name}',
      unlink: 'Cofnij powiązanie',
      unlinkConfirmTitle: 'Cofnąć powiązanie płatności łączonej?',
      unlinkConfirmMessage:
        'Powiązanie {count} faktur z tą transakcją zostanie usunięte. Statusy faktur pozostaną bez zmian.',
      unlinkSuccess: 'Powiązanie cofnięte',
      unlinkError: 'Nie udało się cofnąć powiązania',
      // Sales side ("Dopasuj wpłatę")
      salesButton: 'Dopasuj wpłatę',
      salesDialogTitle: 'Dopasuj wpłatę od klienta',
      salesDialogDescription:
        'Wybierz klienta i walutę, podaj datę i kwotę przelewu — system znajdzie otwarte faktury sprzedażowe, których pozostałe kwoty sumują się do przelewu (różnice do 1,00 w walucie są dopuszczalne). Po akceptacji na każdej fakturze zostanie zarejestrowana wpłata na pełną pozostałą kwotę.',
      customerLabel: 'Klient',
      customerPlaceholder: 'Wybierz klienta',
      noCustomers: 'Brak klientów z otwartymi fakturami',
      currencyLabel: 'Waluta',
      currencyOption: '{currency} — {count} FV, {outstanding}',
      transferDate: 'Data przelewu',
      amountCurrency: 'Kwota ({currency})',
      salesNoMatches:
        'Brak kombinacji faktur pasujących do tej kwoty. Przeszukano {count} otwartych faktur tego klienta.',
      salesNotePlaceholder: 'np. przelew SEPA, ref 8842...',
      salesAccept: 'Powiąż i rozlicz wpłatę',
      salesAcceptSuccess: 'Rozliczono {count} FV wpłatą {amount}',
      salesDiffInfo:
        'Różnica {diff} zostanie pominięta — wszystkie faktury będą rozliczone w całości.',
      diffTooBig: 'Różnica przekracza 1,00 — kombinacja tylko poglądowa, nie można jej powiązać.',
      unlinkConfirmMessageSales:
        'Powiązanie zostanie usunięte, a wpłaty zarejestrowane przez to powiązanie na {count} fakturach zostaną cofnięte (faktury wrócą do nierozliczonych).',
    },

    // Correction invoice (KOR*) marker on invoice lists
    correction: {
      badge: 'Korekta',
      tooltipKor: 'Faktura korygująca — kwoty to różnice względem faktury pierwotnej',
      tooltipKorZal: 'Korekta faktury zaliczkowej',
      tooltipKorRoz: 'Korekta faktury rozliczeniowej',
    },

    notes: {
      title: 'Opisy dla księgowości',
      count: 'Opisy: {count}',
      empty: 'Brak opisów',
      emptyHint: 'Dodaj opis dla zespołu księgowości',
      placeholder: 'np. Proszę zaksięgować jako koszt biurowy...',
      addButton: 'Dodaj opis',
      addSuccess: 'Opis dodany',
      addError: 'Nie udało się dodać opisu',
      updateSuccess: 'Opis zaktualizowany',
      updateError: 'Nie udało się zaktualizować opisu',
      deleteSuccess: 'Opis usunięty',
      deleteError: 'Nie udało się usunąć opisu',
      deleteConfirmTitle: 'Usuń opis',
      deleteConfirmMessage: 'Trwale usunąć ten opis? Tej operacji nie można cofnąć.',
      editedBy: 'edytowano {date} przez {name}',
      edited: 'edytowano',
      saveEdit: 'Zapisz',
      cancelEdit: 'Anuluj',
    },

    tagsEditor: {
      addTag: 'Dodaj tag',
      noTags: 'Brak dostępnych tagów',
      updateError: 'Nie udało się zaktualizować tagów',
    },
  },

  tags: {
    nameRequired: 'Nazwa tagu jest wymagana',
    createSuccess: 'Tag został utworzony',
    updateSuccess: 'Tag został zaktualizowany',
    deleteSuccess: 'Tag został usunięty',
    title: 'Zarządzanie tagami',
    subtitle: 'Tworzenie, edycja i usuwanie tagów dla faktur kosztowych',
    createNew: 'Utwórz nowy tag',
    existingTags: 'Istniejące tagi',
    noTags: 'Brak utworzonych tagów',
    noTagsDescription: 'Utwórz pierwszy tag, aby rozpocząć organizowanie faktur',

    form: {
      name: 'Nazwa tagu',
      namePlaceholder: 'np. Pilne, Ważne...',
      color: 'Kolor',
    },

    actions: {
      add: 'Dodaj',
      edit: 'Edytuj tag',
      delete: 'Usuń tag',
      confirmDelete: 'Czy na pewno chcesz usunąć ten tag?',
      confirmDeleteDescription: 'Tej operacji nie można cofnąć. Faktury z tym tagiem nie będą go już miały.',
    },

    modules: {
      expense: 'FV Koszty',
      sales: 'Sprzedaż',
      contractors: 'Kontrahenci',
    },

    defaults: {
      goods: 'Towary',
      cost: 'Koszty',
      fuel: 'Paliwo',
      geko: 'GEKO',
      gestGoods: 'GEST Towary',
      reInvoice: 'Refaktura',
      serviceImport: 'Import usług',
      b2b: 'B2B',
    },
  },

  contractors: {
    title: 'Kontrahenci',
    titleWithCompany: 'Kontrahenci: {companyName}',
    subtitle: 'Przeglądaj i zarządzaj kontrahentami dla tej firmy',
    searchPlaceholder: 'Szukaj po nazwie kontrahenta, NIP, ID ERP lub adresie',

    role: {
      filterLabel: 'Rola kontrahenta',
      all: 'Wszyscy',
      suppliers: 'Dostawcy',
      customers: 'Odbiorcy',
      supplier: 'Dostawca',
      customer: 'Odbiorca',
      both: 'Dostawca i odbiorca',
      none: 'Brak faktur',
    },
    noContractors: 'Nie znaleziono kontrahentów',
    noContractorsDescription: 'Brak kontrahentów pasujących do wyszukiwania',
    turnoverHidden: 'Obroty widoczne tylko z dostępem do faktur',

    columns: {
      index: '#',
      name: 'Nazwa',
      nip: 'NIP',
      address: 'Adres',
      bankAccounts: 'Rachunki bankowe',
      tags: 'Tagi',
      role: 'Rola',
      invoiceCount: 'FV zakup',
      yearlyTurnoverNet: 'Zakupy (rok) netto',
      monthlyTurnoverNet: 'Zakupy (mies.) netto',
      salesInvoiceCount: 'FV sprzedaż',
      salesYearlyTurnoverNet: 'Sprzedaż (rok) netto',
      lastInvoiceDate: 'Ostatnia FV',
      actions: 'Akcje',
    },

    actions: {
      edit: 'Edytuj',
      editTags: 'Edytuj tagi',
    },

    editModal: {
      title: 'Edytuj kontrahenta',
      name: 'Nazwa',
      nip: 'NIP',
      erpId: 'ID w programie księgowym (ERP)',
      erpIdPlaceholder: 'np. K-000123',
      address: 'Adres',
      bankAccounts: 'Rachunki bankowe',
      tags: 'Tagi',
      addBankAccount: 'Dodaj rachunek bankowy',
    },

    defaultInvoiceTags: 'Domyślne tagi faktur',
    defaultInvoiceTagsHint: 'Nadawane automatycznie fakturom tego kontrahenta przy imporcie',

    documents: {
      upload: 'Wgraj dokument',
      download: 'Pobierz dokument',
      type: 'Typ dokumentu',
      types: {
        nda: 'NDA',
        umowa: 'Umowa',
        aneks: 'Aneks',
        pelnomocnictwo: 'Pełnomocnictwo',
        oferta: 'Oferta',
        inne: 'Inny',
        wz: 'WZ',
        wz_zalacznik: 'Dokumenty do WZ',
      },
      label: 'Etykieta',
      fileName: 'Nazwa pliku',
      documentDate: 'Data dokumentu',
      expiry: 'Wygasa',
      note: 'Notatka',
      deleteConfirm: 'Usunąć ten dokument?',
      expiryWarning: 'Wygasa za {days} dni',
      expired: 'Wygasł',
      typeRequired: 'Wybierz typ dokumentu',
      fileRequired: 'Wybierz plik',
      generateFromTemplate: 'Generuj z szablonu',
      generateTemplate: 'Szablon',
      generateTemplatePlaceholder: 'Wybierz szablon',
      generateNoTemplates: 'Brak opublikowanych szablonów kontrahenta',
      generateSubmit: 'Generuj',
      generateSuccess: 'Dokument wygenerowany',
      generateExpiryHint: 'Opcjonalna — bez daty ważności dokument nie trafi do przypomnień',
      edit: 'Edytuj dane dokumentu',
      editTitle: 'Dane dokumentu',
      saved: 'Zapisano dane dokumentu',
      returns: {
        confirmed: 'Zwrócony ✓',
        pending: 'Zwrócony — do potwierdzenia',
        downloadReturned: 'Pobierz podpisany skan',
        confirm: 'Potwierdź podpis',
        confirmTitle: 'Potwierdzić otrzymanie podpisanego dokumentu?',
        confirmSuccess: 'Zwrot potwierdzony',
      },
    },

    detail: {
      title: 'Kontrahent: {name}',
      back: 'Powrót do kontrahentów',
      tabDashboard: 'Panel',
      tabDocuments: 'Dokumenty',
      year: 'Rok',
      totalTurnoverNet: 'Łączny obrót netto',
      invoiceCount: 'Faktury',
      averageInvoiceNet: 'Średnia faktura netto',
      lastInvoiceDate: 'Ostatnia faktura',
      purchasesNet: 'Zakupy netto (rok)',
      salesNet: 'Sprzedaż netto (rok)',
      balanceNet: 'Saldo (sprzedaż − zakupy)',
      invoiceCounts: 'FV zakup / sprzedaż',
      lastPurchaseDate: 'Ostatnia FV zakupowa',
      lastSaleDate: 'Ostatnia FV sprzedażowa',
      purchasesSeries: 'Zakupy netto',
      salesSeries: 'Sprzedaż netto',
      purchaseShare: 'Udział w kosztach %',
      salesShare: 'Udział w sprzedaży %',
      monthlyTurnover: 'Obroty miesięczne: zakupy vs sprzedaż',
      monthlyShare: 'Udział w obrotach firmy',
      viewInvoices: 'Zobacz faktury',
      noData: 'Brak danych za ten rok',
      netAmount: 'Netto',
      grossAmount: 'Brutto',
    },
  },

  dashboard: {
    title: 'Panel',
    titleWithCompany: 'Panel: {companyName}',
    year: 'Rok',
    totalCosts: 'Łączne koszty',
    totalVat: 'Łączny VAT',
    currentMonthGross: 'Brutto ({month})',
    currentMonthVat: 'VAT ({month})',
    grossAmount: 'Brutto',
    vatAmount: 'VAT',
    invoiceCount: 'Faktury',
    invoicesMonth: 'Faktury ({month})',
    averageInvoice: 'Średnia faktura',
    topPaymentMethod: 'Najczęstsza forma płatności',
    monthlyCosts: 'Koszty miesięczne',
    monthlyTagCosts: 'Miesięczne koszty netto wg tagów',
    monthlyPaymentMethodCosts: 'Miesięczne koszty wg formy płatności',
    unpaidByMonth: 'Niezapłacone wg miesiąca',
    unpaidTotal: 'Niezapłacone (rok)',
    unpaidCurrentMonth: 'Niezapłacone ({month})',
    noData: 'Brak danych za ten rok',
    paymentMethods: {
      transfer: 'Przelew',
      card: 'Karta',
      cash: 'Gotówka',
      other: 'Inne',
    },
    financial: {
      revenueYtd: 'Przychód (rok)',
      costYtd: 'Koszty (rok)',
      margin: 'Marża (rok)',
      marginOfRevenue: '{pct}% przychodu',
      mom: '{pct}% m/m',
      momTitle: 'Zmiana przychodu miesiąc do miesiąca',
      vatPosition: 'Pozycja VAT',
      vatPayable: 'do zapłaty',
      vatRefund: 'do zwrotu',
      vatNeutral: 'neutralna',
      receivables: 'Należności',
      payables: 'Zobowiązania',
      overdue: 'Po terminie: {amount}',
      allOnTime: 'Wszystko w terminie',
      revenueVsCosts: 'Przychody vs koszty',
      revenue: 'Przychód',
      costs: 'Koszty',
      marginDelta: 'Marża',
      marginPctTitle: 'Marża % (miesięcznie)',
      marginPct: 'Marża %',
      cashFlow: 'Przepływy pieniężne',
      cashIn: 'Wpływy',
      cashOut: 'Wypływy',
      cashNet: 'Netto',
      aging: 'Wiekowanie należności i zobowiązań',
      agingReceivables: 'Należności',
      agingPayables: 'Zobowiązania',
      bucketCurrent: 'W terminie',
      bucket30: '1–30 dni',
      bucket60: '31–60 dni',
      bucket90: '61–90 dni',
      bucketOlder: '90+ dni',
      topCustomers: 'Top 5 odbiorców',
      topVendors: 'Top 5 dostawców',
      invoicesCount: 'Faktury: {count}',
      noCounterparties: 'Brak danych',
      noSales: 'Brak danych sprzedażowych — podłącz KSeF, aby zobaczyć przychody i marżę.',
      costSection: 'Analiza kosztów',
      showCostDetails: 'Pokaż szczegóły kosztów',
      hideCostDetails: 'Ukryj szczegóły kosztów',
    },
    health: {
      section: 'Kondycja spółki',
      sectionSubtitle: 'Przychody, koszty, marża, gotówka i terminowość płatności',
    },
    people: {
      section: 'Ludzie',
      sectionSubtitle: 'Ile naprawdę kosztują pracownicy i gdzie idą te pieniądze',
      allEmployees: 'Wszyscy pracownicy',
      employerCostYtd: 'Koszt pracodawcy (rok)',
      employerCostHint: 'Brutto + składki pracodawcy',
      netYtd: 'Wynagrodzenia netto (rok)',
      netHint: 'To, co trafia „na rękę”',
      taxWedgeYtd: 'Danina (ZUS + PIT, rok)',
      taxWedgeShare: '{pct}% kosztu pracodawcy',
      avgCostPerEmployee: 'Koszt na pracownika',
      avgCostHint: 'Średnio, narastająco w roku',
      headcount: 'Liczba pracowników',
      headcountHint: 'Aktywni dziś (bez B2B)',
      headcountValue: '{count} os.',
      costStackTitle: 'Gdzie idą pieniądze — koszt pracownika miesięcznie',
      costStackHint:
        'Każdy słupek to całkowity koszt pracodawcy w danym miesiącu, rozbity na to, co dostaje pracownik, i na daninę.',
      costBaseTitle: 'Koszt ludzi vs koszty faktur',
      costBaseHint: 'Z czego składa się baza kosztowa spółki w każdym miesiącu.',
      byTypeTitle: 'Koszt pracodawcy wg typu umowy',
      byTypeHint: 'Suma po typach umów równa się kosztowi pracodawcy (bez B2B).',
      net: 'Netto',
      pit: 'PIT',
      employeeZus: 'ZUS pracownika',
      health: 'Składka zdrowotna',
      employerZus: 'ZUS pracodawcy',
      gross: 'Brutto',
      employerCostTotal: 'Całkowity koszt pracodawcy',
      payrollCost: 'Koszt pracodawcy (etaty)',
      b2bCost: 'B2B (w kosztach faktur)',
      otherInvoiceCost: 'Pozostałe koszty faktur',
      costBaseTotal: 'Baza kosztowa razem',
      peopleShare: 'Udział ludzi',
      b2bTooltipNote:
        'B2B jest już zawarte w kosztach faktur — pokazujemy je osobno, ale nie dodajemy do kosztu pracodawcy.',
      b2bFootnote:
        'Koszt kontraktorów B2B trafia do spółki jako faktura zakupowa, więc jest już częścią kosztów faktur. Wydzielamy go, żeby było widać, ile z bazy kosztowej to ludzie — nigdy nie doliczamy go do kosztu pracodawcy (byłoby to podwójne liczenie).',
      incompleteTitle: '{count} prac. bez stawki lub typu umowy — koszty niepełne',
      incompleteBody:
        'Ci pracownicy zostali pominięci w wyliczeniach, więc kwoty w tej sekcji są zaniżone.',
      incompleteCta: 'Uzupełnij dane',
      noPayroll: 'Brak danych płacowych za ten rok',
    },
    projects: {
      section: 'Projekty',
      sectionSubtitle: 'Rentowność projektów — zysk i strata na tle przychodów i kosztów',
      allProjects: 'Wszystkie projekty',
      plTitle: 'Zysk i strata wg projektu',
      plHint: 'Słupki w prawo to zysk, w lewo — strata. Najedź, aby zobaczyć przychód, koszt i marżę.',
      profit: 'Zysk',
      loss: 'Strata',
      revenue: 'Przychód',
      cost: 'Koszt',
      margin: 'Marża',
      colProject: 'Projekt',
      othersLabel: 'Pozostałe ({count})',
      rollupTooltip: 'Zbiorczo {count} mniejszych projektów.',
      rollupNote:
        'Pokazano {shown} projektów o największym wyniku (co do wartości bezwzględnej). Pozostałe {count} zwinięto do jednej pozycji „Pozostałe”.',
      empty: 'Brak projektów. Utwórz projekt, aby śledzić jego rentowność.',
    },
    counterparties: {
      section: 'Kontrahenci',
      sectionSubtitle: 'Najwięksi odbiorcy i dostawcy w wybranym roku',
    },
  },

  fx: {
    title: 'Kursy NBP',
    subtitle: 'Trend 30 dni',
    effectiveDate: 'z dnia {date}',
    change30dLabel: 'zmiana 30 dni',
    loading: 'Wczytywanie kursów…',
    loadError: 'Nie udało się pobrać kursów NBP',
    empty: 'Brak danych o kursach',
    plnEquivalent: '≈ {amount} PLN · kurs NBP {rate} z {date}',
    approxPln: '≈ {amount} PLN',
    noRate: 'brak kursu',
  },

  sales: {
    title: 'Sprzedaż',
    titleWithCompany: 'Sprzedaż: {companyName}',
    subtitle: 'Przychody i faktury sprzedażowe zsynchronizowane z KSeF',
    totalNet: 'Przychód (rok)',
    currentMonthNet: 'Przychód ({month})',
    invoiceCount: 'Liczba faktur',
    avgNet: 'Średnia faktura',
    monthlyRevenue: 'Przychód miesięczny',
    netAmount: 'Netto',
    noData: 'Brak faktur sprzedażowych. Dane pojawią się po synchronizacji z KSeF.',
    searchPlaceholder: 'Szukaj po numerze faktury lub nabywcy',
    noInvoices: 'Nie znaleziono faktur',
    noInvoicesDescription: 'Brak faktur pasujących do wybranych filtrów',
    columns: {
      customer: 'Nabywca',
      invoiceNumber: 'Numer faktury',
      saleDate: 'Data sprzedaży',
      net: 'Netto',
      vat: 'VAT',
      gross: 'Brutto',
      ksefNumber: 'Nr KSeF',
      tags: 'Tagi',
      settlement: 'Rozliczenie',
      project: 'Projekt',
      actions: 'Akcje',
    },

    filters: {
      clearAll: 'Wyczyść filtry',
    },

    settlement: {
      filterLabel: 'Rozliczenie',
      all: 'Wszystkie',
      unpaid: 'Nieopłacona',
      partial: 'Częściowo',
      paid: 'Zapłacona',
      overdue: 'Po terminie',
      overdueDays: 'Po terminie ({days} dni)',
      outstandingLeft: 'pozostało {amount}',
    },

    payments: {
      register: 'Zarejestruj wpłatę',
      dialogTitle: 'Zarejestruj wpłatę',
      dialogDescription: 'Faktura {invoiceNumber} — do zapłaty {outstanding}',
      amountLabel: 'Kwota ({currency})',
      dateLabel: 'Data wpłaty',
      noteLabel: 'Notatka (opcjonalnie)',
      notePlaceholder: 'np. tytuł przelewu...',
      submit: 'Zapisz wpłatę',
      success: 'Wpłata zarejestrowana',
      overpaymentWarning: 'Wpłata zarejestrowana — faktura jest teraz nadpłacona',
      error: 'Nie udało się zarejestrować wpłaty',
      invalidAmount: 'Kwota musi być większa od 0',
      historyTitle: 'Historia wpłat',
      noPayments: 'Brak zarejestrowanych wpłat',
      paidLabel: 'Zapłacono',
      outstandingLabel: 'Do zapłaty',
      deleteConfirmTitle: 'Usuń wpłatę',
      deleteConfirmMessage: 'Trwale usunąć ten zapis wpłaty? Tej operacji nie można cofnąć.',
      deleteSuccess: 'Wpłata usunięta',
      deleteError: 'Nie udało się usunąć wpłaty',
      recordedBy: 'zarejestrował(a) {name}',
    },

    receivables: {
      title: 'Należności',
      subtitle: 'Nabywcy z otwartymi fakturami sprzedażowymi',
      customer: 'Nabywca',
      invoiceCount: 'Faktury',
      outstanding: 'Do zapłaty',
      overdue: 'Po terminie',
      oldestDueDate: 'Najstarszy termin',
      empty: 'Brak nieopłaconych należności',
      rowHint: 'Kliknij, aby przefiltrować listę faktur',
    },

    // Raport sprzedaży: FV z danego miesiąca sprzedaży (brak daty sprzedaży =
    // data wystawienia), posortowane po dacie wystawienia rosnąco.
    report: {
      button: 'Raport',
      title: 'Raport sprzedaży — {month}',
      monthLabel: 'Miesiąc sprzedaży',
      yearLabel: 'Rok sprzedaży',
      copy: 'Kopiuj do schowka',
      copied: 'Skopiowano do schowka',
      copyError: 'Nie udało się skopiować do schowka',
      copyValue: 'Kopiuj wartość',
      count: 'Liczba faktur: {count}',
      emptyDescription: 'Brak faktur sprzedażowych w wybranym miesiącu',
      columns: {
        lp: 'Lp.',
        saleDate: 'Data sprzedaży',
        issueDate: 'Data wystawienia',
        invoiceNumber: 'Numer FV',
        ksefNumber: 'Numer KSeF',
        customer: 'Klient',
        netAmount: 'Kwota netto',
        currency: 'Waluta',
      },
    },

    // Zestawienie należności klienta w PDF (wyciąg / potwierdzenie salda).
    statement: {
      button: 'Zestawienie PDF',
      title: 'Zestawienie należności klienta',
      description: 'Wygeneruj PDF ze stanem rozliczeń klienta na wybrany dzień.',
      descriptionContractor: 'Wygeneruj PDF ze stanem rozliczeń klienta {name} na wybrany dzień.',
      customer: 'Klient',
      customerPlaceholder: 'Wybierz klienta',
      noCustomers: 'Brak klientów z fakturami sprzedaży',
      type: 'Rodzaj dokumentu',
      typeStatement: 'Wyciąg dla klienta',
      typeBalanceConfirmation: 'Potwierdzenie salda',
      lang: 'Język dokumentu',
      langPl: 'Polski',
      langEn: 'Angielski',
      range: 'Zakres dat',
      rangeYtd: 'Od początku roku',
      rangeCustom: 'Własny',
      from: 'Od',
      to: 'Do (stan na dzień)',
      scope: 'Zakres treści',
      scopeOpen: 'Tylko należności',
      scopeAll: 'Także zapłacone',
      asOfHint: 'Wpłaty z datą późniejszą niż koniec zakresu nie są uwzględniane.',
      download: 'Pobierz PDF',
      downloaded: 'Pobrano zestawienie',
      loadFailed: 'Nie udało się pobrać listy klientów',
    },
  },

  // Projekty (Task B3): rozliczenie kosztów i przychodów per projekt (P&L)
  projects: {
    title: 'Projekty',
    titleWithCompany: 'Projekty: {companyName}',
    subtitle: 'Zysk i strata projektów — koszty i sprzedaż razem',
    newProject: 'Nowy projekt',
    noProjects: 'Brak projektów',
    noProjectsDescription: 'Utwórz pierwszy projekt, aby przypisywać do niego faktury',
    loadError: 'Nie udało się wczytać projektów',
    partialBadge: 'częściowo',
    partialTooltip:
      'Część pozycji ma własne projekty — liczą się do nich, a pozostała wartość faktury do projektu całej faktury',
    columns: {
      name: 'Nazwa',
      status: 'Status',
      cost: 'Koszty',
      revenue: 'Przychód',
      profit: 'Zysk/Strata',
      margin: 'Marża %',
      invoiceCount: 'Faktury',
      actions: 'Akcje',
    },
    status: {
      active: 'Aktywny',
      closed: 'Zamknięty',
    },
    form: {
      createTitle: 'Nowy projekt',
      editTitle: 'Edytuj projekt',
      name: 'Nazwa',
      namePlaceholder: 'np. Budowa hali',
      code: 'Kod (opcjonalnie)',
      codePlaceholder: 'np. HALA-1',
      description: 'Opis (opcjonalnie)',
      descriptionPlaceholder: 'Czego dotyczy ten projekt...',
      color: 'Kolor',
      nameRequired: 'Nazwa projektu jest wymagana',
      submitCreate: 'Utwórz projekt',
      submitSave: 'Zapisz zmiany',
    },
    actions: {
      edit: 'Edytuj',
      close: 'Zamknij projekt',
      reopen: 'Otwórz projekt',
    },
    createSuccess: 'Projekt utworzony',
    createError: 'Nie udało się utworzyć projektu',
    updateSuccess: 'Projekt zaktualizowany',
    updateError: 'Nie udało się zaktualizować projektu',
    closeSuccess: 'Projekt zamknięty',
    reopenSuccess: 'Projekt otwarty ponownie',
    statusError: 'Nie udało się zmienić statusu projektu',
    closeConfirmTitle: 'Zamknij projekt',
    closeConfirmMessage:
      'Zamknąć projekt „{name}"? Przypisania i historia pozostaną bez zmian — projekt przestanie tylko pojawiać się na listach wyboru.',
    analytics: {
      kpiCost: 'Koszty (netto)',
      kpiRevenue: 'Przychód (netto)',
      kpiProfit: 'Zysk/Strata',
      kpiMargin: 'Marża',
      noRevenue: 'brak przychodu',
      invoiceCounts: '{purchase} kosztowych / {sale} sprzedażowych',
      monthlyTitle: 'Koszty vs przychód (ostatnie 12 miesięcy)',
      topCostContractors: 'Najwięksi dostawcy (koszty)',
      topRevenueCustomers: 'Najwięksi odbiorcy (przychód)',
      noCounterparties: 'Brak danych',
      invoicesTitle: 'Faktury przypisane do projektu',
      direction: 'Kierunek',
      directionPurchase: 'Koszt',
      directionSale: 'Sprzedaż',
      counterparty: 'Kontrahent',
      invoiceNumber: 'Numer',
      date: 'Data',
      netPln: 'Netto PLN',
      scope: 'Zakres',
      scopeWhole: 'cała faktura',
      scopeLines: 'pozycje',
      noInvoices: 'Brak faktur przypisanych do tego projektu',
      loadError: 'Nie udało się wczytać analityki projektu',
      notFound: 'Nie znaleziono projektu',
      backToList: 'Wróć do projektów',
    },
    selector: {
      label: 'Projekt',
      none: 'Brak',
      noProject: 'Bez projektu',
      closedSuffix: '(zamknięty)',
      inherits: 'Dziedziczy:',
      assignError: 'Nie udało się zaktualizować przypisania do projektu',
    },
    autoBadge: 'auto',
    autoTooltip:
      'Przypisane automatycznie regułą tagu — usunięcie pasującego taga cofnie przypisanie',
    correctionAutoTooltip:
      'Projekt odziedziczony z faktury pierwotnej — korekta automatycznie podąża za fakturą, którą koryguje.',
    tagRules: {
      sectionTitle: 'Reguły tagów',
      description:
        'Faktury oznaczone którymkolwiek z wybranych tagów będą automatycznie przypisywane do tego projektu. Usunięcie ostatniego pasującego taga cofa automatyczne przypisanie; ręcznych przypisań reguły nie zmieniają.',
      expenseGroup: 'Tagi kosztowe',
      salesGroup: 'Tagi sprzedażowe',
      noTags: 'Ta firma nie ma jeszcze tagów',
      loadError: 'Nie udało się wczytać reguł tagów',
      saveError: 'Nie udało się zapisać reguł tagów',
      applyConfirmTitle: 'Zastosuj reguły do istniejących faktur',
      applyConfirmMessage:
        'Znaleziono {matched} faktur z tymi tagami. Przypisać {assigned} do projektu? ({conflicts} pominięte — należą już do innego projektu)',
      applyConfirmAction: 'Przypisz',
      applySuccess: 'Przypisano {assigned} faktur(y) do projektu ({conflicts} pominięte)',
      applyError: 'Nie udało się zastosować reguł tagów',
      noMatches: 'Żadna istniejąca faktura nie ma wybranych tagów',
    },
  },

  export: {
    mbank: {
      loadFailed: 'Nie udało się załadować szczegółów faktur',
      merged: 'Scalono płatności. {regular} zwykłych, {unchanged} bez zmian.',
      unmerged: 'Rozłączono płatności.',
      noAccountForCurrency: 'Brak firmowego rachunku dla waluty {currency}',
      generated: 'Plik eksportu wygenerowany pomyślnie',
      generateFailed: 'Nie udało się wygenerować eksportu',
      executionDatePast: 'Data realizacji jest w przeszłości — wybierz nową datę',
      missingAccountOrName: 'Niektóre płatności nie mają numeru rachunku lub nazwy kontrahenta',
      swiftRequiresBic: 'Płatności SWIFT wymagają kodu SWIFT/BIC',
      // Dlaczego przycisk eksportu jest nieaktywny — operator dostawał
      // martwy przycisk bez wyjaśnienia (faktury zagraniczne, audyt 2026-07).
      blockers: {
        title: 'Eksport zablokowany',
        warningsTitle: 'Sprawdź przed eksportem',
        nothingToExport: 'Brak płatności do eksportu',
        noDebtorAccount: 'Brak firmowego rachunku w walucie {currency} — dodaj go w ustawieniach firmy',
        noBankAccount: 'Płatności bez rachunku odbiorcy: {count}',
        noContractorName: 'Płatności bez nazwy kontrahenta: {count}',
        mppIncomplete: 'Płatności MPP bez NIP kontrahenta lub kwoty VAT: {count}',
        swiftBic: 'Płatności z brakującym lub błędnym kodem SWIFT/BIC: {count}',
        missingCityCountry: 'Płatności zagraniczne bez miasta lub kraju: {count}',
        invalidDate: 'Płatności z nieprawidłową datą płatności: {count} (albo zaznacz „Override all payment dates”)',
        invalidCountry: 'Płatności, których kraj nie jest 2-literowym kodem ISO: {count} — kraj zostanie pominięty w pliku',
      },
      reimbursements: {
        title: 'Zwroty dla pracowników',
        employee: 'Pracownik',
        account: 'Konto',
        transferTitle: 'Tytuł przelewu',
        amount: 'Kwota',
      },
    },
    formats: {
      xlsx: 'Excel (XLSX)',
      csv: 'CSV',
      pdf: 'PDF',
    },
  },

  pagination: {
    firstPage: 'Pierwsza strona',
    prevPage: 'Poprzednia strona',
    nextPage: 'Następna strona',
    lastPage: 'Ostatnia strona',
    page: 'Strona',
    of: 'z',
    itemsPerPage: 'Na stronie',
    showing: 'Wyświetlanie {from} do {to} z {total}',
  },

  selectionSummary: {
    title: 'Zaznaczone ({count}) —',
    net: 'netto',
    gross: 'brutto',
    missingRate: 'bez {count} poz. — brak kursu',
  },

  ksef: {
    metadata: {
      verification: 'Weryfikacja KSeF',
      verifyOnKsef: 'Weryfikuj na ksef.mf.gov.pl',
      copy: 'Kopiuj',
      copied: 'Skopiowano',
      invoiceData: 'Dane faktury',
      form: 'Formularz',
      rodzaj: 'Rodzaj',
      wariant: 'Wariant',
      wariantN: 'Wariant {n}',
      placeOfIssue: 'Miejsce wystawienia',
      xmlDate: 'Data XML',
      system: 'System',
      period: 'Okres',
      correction: 'Korekta',
      corrects: 'Koryguje fakturę',
      correctionReason: 'Przyczyna korekty',
      buyer: 'Nabywca',
      name: 'Nazwa',
      nip: 'NIP',
      clientNo: 'Nr klienta',
      address: 'Adres',
      email: 'Email',
      phone: 'Telefon',
      thirdParty: 'Podmiot trzeci',
      vatBreakdown: 'Rozbicie wg stawek VAT',
      rate: 'Stawka',
      net: 'Netto',
      vat: 'VAT',
      bankAccounts: 'Rachunki bankowe',
      paid: 'Zapłacono',
      otherPayment: 'Płatność inna: {value}',
      annotations: 'Adnotacje',
      additionalDescription: 'Dodatkowy opis',
      order: 'Zamówienie',
      orderValue: 'Wartość: {value}',
      orderExternalNo: 'Nr zewn.: {value}',
      orderInternalNo: 'Nr wewn.: {value}',
      deliveryNotes: 'Dokumenty WZ',
      settlement: 'Rozliczenie',
      charges: 'Obciążenia',
      deductions: 'Odliczenia',
      reason: 'Powód',
      totalCharges: 'Suma obciążeń',
      totalDeductions: 'Suma odliczeń',
      amountDue: 'Do zapłaty',
      amountToRefund: 'Do rozliczenia (nadpłata)',
      paymentDate: 'Data wpłaty',
      amount: 'Kwota',
      sellerContact: 'Kontakt sprzedawcy',
      rodzajLabels: {
        VAT: 'Faktura VAT',
        KOR: 'Korekta',
        ZAL: 'Zaliczkowa',
        ROZ: 'Rozliczeniowa',
        UPR: 'Uproszczona',
        ROD: 'Paragon fiskalny',
        WEW: 'Wewnętrzna',
        VAT_MP: 'Marża',
        VAT_RR: 'Rolnik ryczałtowy',
      },
      wariantLabels: {
        '1': 'Standardowa',
        '2': 'Korygująca',
        '3': 'Zaliczkowa',
        '4': 'Końcowa',
        '5': 'Uproszczona',
        '6': 'Pro forma',
      },
      adnotacjeLabels: {
        P_16: 'Metoda kasowa',
        P_17: 'Samofakturowanie',
        P_18: 'Marża procedura',
        P_18A: 'Marża biuro podróży',
        P_19: 'Zwolnienie z VAT',
        P_19A: 'Podstawa prawna zwolnienia',
        P_19B: 'Przepis dyrektywy',
        P_19C: 'Inna podstawa prawna',
        P_20: 'Odwrotne obciążenie krajowe',
        P_21: 'Odwrotne obciążenie UE',
        P_22: 'Nowe środki transportu WDT',
        P_23: 'Dostawa poza UE',
        P_106E_2: 'Procedura trójstronna',
        P_106E_3: 'Kraj trzeciej strony',
        zwolnienieP19A: 'Podstawa prawna zwolnienia',
      },
    },
    enabledToast: 'KSeF włączony',
    disabledToast: 'KSeF wyłączony',
    configUpdateFailed: 'Nie udało się zaktualizować konfiguracji KSeF',
    environmentUpdateFailed: 'Nie udało się zaktualizować środowiska',
    syncDateUpdateFailed: 'Nie udało się zaktualizować daty synchronizacji',
    certificateRemoved: 'Usunięto certyfikat',
    certificateRemoveFailed: 'Nie udało się usunąć certyfikatu',
    certificateUploadFailed: 'Nie udało się przesłać certyfikatu',
    syncCompletedWithErrors: 'Synchronizacja zakończona z błędami: {errors}',
    title: 'Integracja KSeF',
    enabled: 'Włączona',
    environment: 'Środowisko',
    environmentTest: 'Testowe',
    environmentProduction: 'Produkcyjne',
    syncFromDate: 'Synchronizuj od daty',
    certificate: 'Certyfikat',
    certificateUploaded: 'Certyfikat przesłany',
    certificateNotUploaded: 'Brak certyfikatu',
    certificateFingerprint: 'Odcisk',
    certificateExpiry: 'Wygasa',
    certificatePassword: 'Hasło (dla zaszyfrowanego klucza lub .p12/.pfx)',
    certificateFile: 'Plik certyfikatu (.crt, .pem, .p12, .pfx)',
    privateKeyFile: 'Plik klucza prywatnego (.key, .pem)',
    uploadCertificate: 'Prześlij certyfikat',
    removeCertificate: 'Usuń certyfikat',
    testConnection: 'Testuj połączenie',
    testConnectionSuccess: 'Połączenie udane',
    testConnectionFailed: 'Połączenie nieudane',
    syncNow: 'Synchronizuj teraz',
    syncSuccess: 'Synchronizacja KSeF zakończona: {imported} zaimportowanych, {skipped} pominiętych',
    syncError: 'Synchronizacja KSeF nie powiodła się',
    lastSync: 'Ostatnia synchronizacja',
    lastSyncStatus: 'Status',
    lastSyncError: 'Błąd',
    syncStatusSuccess: 'Sukces',
    syncStatusError: 'Błąd',
    syncStatusInProgress: 'W trakcie',
  },

  fiscal: {
    title: 'Fiskalizacja',
    comingSoon: 'Wkrótce dostępne',
    tabs: {
      dashboard: 'Panel',
      invoices: 'Faktury',
      receipts: 'Paragony',
      printers: 'Drukarki',
      settings: 'Ustawienia',
    },
    environment: {
      demo: 'Demo',
      test: 'Test',
      production: 'Produkcja',
    },
    environmentDefaultSuffix: '(domyślne)',
    // Feminine agreement ("faktura ... potwierdzona/odrzucona").
    invoiceStatus: {
      QUEUED: 'W kolejce',
      PROCESSING: 'Przetwarzana',
      RETRY_WAIT: 'Oczekuje na ponowienie',
      CONFIRMED: 'Potwierdzona',
      REJECTED: 'Odrzucona',
    },
    // Masculine agreement ("paragon ... potwierdzony/odrzucony/wysłany").
    receiptStatus: {
      QUEUED: 'W kolejce',
      DISPATCHED: 'Wysłany',
      CONFIRMED: 'Potwierdzony',
      REJECTED: 'Odrzucony',
    },
    columns: {
      status: 'Status',
      number: 'Numer',
      buyer: 'Nabywca',
      amount: 'Kwota',
      ksefNumber: 'Numer KSeF',
      printer: 'Drukarka',
      mode: 'Tryb',
      jpkid: 'JPK ID',
      createdAt: 'Utworzono',
      actions: 'Akcje',
      retry: 'Ponów',
      details: 'Szczegóły',
    },
    accessDenied: {
      description: 'Nie masz uprawnień do modułu fiskalizacji w tej firmie.',
    },
    dashboard: {
      invoicesToday: 'FV dziś',
      invoicesMonth: 'FV w tym miesiącu',
      receiptsToday: 'Paragony dziś',
      receiptsMonth: 'Paragony w tym miesiącu',
      rejectedTotal: 'Odrzucone łącznie',
      printersOnline: 'Drukarki online',
      chartInvoicesTitle: 'Faktury — ostatnie 30 dni',
      chartReceiptsTitle: 'Paragony — ostatnie 30 dni',
      confirmed: 'Potwierdzone',
      rejected: 'Odrzucone',
      notConfiguredTitle: 'Moduł fiskalizacji nieskonfigurowany',
      notConfiguredDescription:
        'Włącz integrację KSeF i/lub paragony fiskalne w ustawieniach, aby zobaczyć statystyki.',
      goToSettings: 'Przejdź do ustawień',
    },
    invoices: {
      searchPlaceholder: 'Szukaj po numerze lub nabywcy…',
      statusAll: 'Wszystkie statusy',
      limitLabel: 'Limit',
      refresh: 'Odśwież',
      empty: {
        title: 'Brak faktur fiskalnych',
        description: 'Nie znaleziono faktur pasujących do wybranych filtrów',
      },
      detail: {
        title: 'Faktura {number}',
        status: 'Status',
        error: 'Błąd',
        attempts: 'Próby: {count}',
        nextRetryAt: 'Kolejna próba: {date}',
        buyer: 'Nabywca',
        amount: 'Kwota',
        ksefNumber: 'Numer KSeF',
        qrTitle: 'Kod QR (weryfikacja KSeF)',
        verificationLink: 'Otwórz stronę weryfikacji KSeF',
        downloadXml: 'Pobierz XML',
        downloadUpo: 'Pobierz UPO',
        upoUnavailable: 'UPO jeszcze niedostępne',
        eventsTitle: 'Historia zdarzeń',
        noEvents: 'Brak zdarzeń',
        retry: 'Ponów',
        retryConfirmTitle: 'Ponowić przetwarzanie faktury?',
        retryConfirmMessage: 'Faktura {number} zostanie ponownie wysłana do KSeF.',
        retrySuccess: 'Ponowiono przetwarzanie faktury',
        retryConflict: 'Nie można teraz ponowić tej faktury (status się zmienił)',
        retryError: 'Nie udało się ponowić faktury',
        downloadError: 'Nie udało się pobrać pliku',
      },
    },
    receipts: {
      printerAll: 'Wszystkie drukarki',
      refresh: 'Odśwież',
      mode: {
        eparagon: 'E-paragon',
        paper: 'Papierowy',
      },
      empty: {
        title: 'Brak paragonów fiskalnych',
        description: 'Nie znaleziono paragonów pasujących do wybranych filtrów',
      },
      detail: {
        title: 'Paragon {jpkid}',
        status: 'Status',
        error: 'Błąd',
        printer: 'Drukarka',
        mode: 'Tryb',
        amount: 'Kwota',
        jpkid: 'JPK ID',
        downloadPdf: 'Pobierz PDF',
        pdfUnavailable: 'PDF jeszcze niedostępny',
        eDocumentTitle: 'E-dokument',
        eDocumentStatus: 'Status',
        eDocumentCode: 'Kod',
        eventsTitle: 'Historia zdarzeń',
        noEvents: 'Brak zdarzeń',
        retry: 'Ponów',
        retryConfirmTitle: 'Ponowić przetwarzanie paragonu?',
        retryConfirmMessage: 'Paragon zostanie ponownie wysłany do drukarki fiskalnej.',
        retrySuccess: 'Ponowiono przetwarzanie paragonu',
        retryConflict: 'Nie można teraz ponowić tego paragonu (status się zmienił)',
        retryError: 'Nie udało się ponowić paragonu',
        downloadError: 'Nie udało się pobrać pliku',
      },
    },
    printers: {
      refresh: 'Odśwież',
      add: 'Dodaj drukarkę',
      addTitle: 'Nowa drukarka',
      editTitle: 'Edytuj drukarkę: {code}',
      codeLabel: 'Kod',
      codeHint: 'Małe litery, cyfry i myślniki, 1-32 znaki (np. kasa-1)',
      codeInvalid: 'Kod musi składać się z małych liter, cyfr i myślników (1-32 znaki)',
      nameLabel: 'Nazwa',
      namePlaceholder: 'np. Kasa fiskalna – recepcja',
      nameRequired: 'Nazwa jest wymagana',
      timeInvalid: 'Godzina musi być w formacie HH:MM',
      dailyReportModeLabel: 'Tryb raportu dobowego',
      dailyReportMode: {
        central: 'Centralny',
        centralHint: 'Serwer zleca agentowi wykonanie raportu dobowego o zaplanowanej godzinie.',
        agent: 'Lokalny (agent)',
        agentHint: 'Agent samodzielnie decyduje, kiedy wykonać raport dobowy, według własnego harmonogramu.',
      },
      dailyReportTimeLabel: 'Godzina raportu dobowego',
      activeLabel: 'Aktywna',
      create: 'Utwórz',
      save: 'Zapisz',
      createSuccess: 'Utworzono drukarkę',
      createFailed: 'Nie udało się utworzyć drukarki',
      updateSuccess: 'Zaktualizowano drukarkę',
      updateFailed: 'Nie udało się zaktualizować drukarki',
      edit: 'Edytuj drukarkę',
      regenerateToken: 'Nowy token',
      delete: 'Usuń drukarkę',
      deleteConfirmTitle: 'Usunąć drukarkę {code}?',
      deleteConfirmMessage: 'Ta operacja jest nieodwracalna. Token dostępowy agenta przestanie działać.',
      deleteSuccess: 'Usunięto drukarkę',
      deleteFailed: 'Nie udało się usunąć drukarki',
      regenerateConfirmTitle: 'Wygenerować nowy token dla {code}?',
      regenerateConfirmMessage:
        'Dotychczasowy token natychmiast przestanie działać — agent straci połączenie, dopóki nie zostanie zaktualizowany nowym tokenem.',
      regenerateSuccess: 'Wygenerowano nowy token',
      regenerateFailed: 'Nie udało się wygenerować tokenu',
      tokenRevealTitle: 'Token agenta — drukarka {code}',
      tokenRevealDescription: 'Nowy token dostępu agenta drukarki fiskalnej.',
      tokenRevealWarning:
        'Ten token zostanie pokazany tylko raz. Skopiuj go teraz i zapisz w bezpiecznym miejscu — po zamknięciu tego okna nie będzie można go odzyskać.',
      copyToken: 'Kopiuj token',
      tokenCopied: 'Skopiowano token',
      copySnippet: 'Kopiuj polecenie',
      snippetCopied: 'Skopiowano polecenie',
      dockerSnippetTitle: 'Gotowe polecenie uruchomienia agenta',
      dockerSnippetHint: 'Podmień <IP-drukarki> na adres IP drukarki fiskalnej w sieci lokalnej.',
      close: 'Rozumiem, zamknij',
      empty: {
        title: 'Brak skonfigurowanych drukarek',
        description: 'Dodaj pierwszą drukarkę, aby zacząć rejestrować paragony fiskalne.',
      },
      deviceStatus: 'Status urządzenia',
      deviceStatusUnknown: 'Brak danych z agenta',
      taxRatesTitle: 'Stawki VAT',
      taxRatesEmpty: 'Brak danych o stawkach',
      protectedMemoryFree: 'Wolna pamięć chroniona',
      queueDepth: 'Kolejka zadań',
      lastDailyReport: 'Ostatni raport dobowy',
      lastDailyReportNever: 'Nigdy',
      dailyReportOverdue: 'Zaległy raport dobowy',
      blackout: 'Blackout (dzień zamknięty)',
      agentVersion: 'Wersja agenta',
      lastHeartbeat: 'Ostatni sygnał',
      lastHeartbeatNever: 'Brak sygnału',
      online: 'Online',
      offline: 'Offline',
      inactive: 'Nieaktywna',
      loadFailed: 'Nie udało się pobrać listy drukarek',
    },
    settings: {
      loadFailed: 'Nie udało się pobrać ustawień modułu fiskalizacji',
      readOnlyNote: 'Tylko administrator firmy może zmieniać te ustawienia.',
      moduleTitle: 'Włączenie modułu',
      moduleEnabled: 'Moduł fiskalizacji włączony',
      moduleHint:
        'Wyłączenie zatrzymuje wysyłkę nowych faktur i paragonów do KSeF oraz drukarek — dane historyczne pozostają widoczne.',
      environmentTitle: 'Środowisko KSeF',
      environmentHint: {
        demo: 'Środowisko testowe KSeF bez powiązania z rzeczywistymi danymi firmy — do prób integracyjnych.',
        test: 'Środowisko testowe KSeF z danymi zbliżonymi do produkcyjnych — do akceptacji przed wdrożeniem.',
        production: 'Rzeczywiste środowisko KSeF — wystawione faktury są prawnie wiążące.',
      },
      environmentConfirmTitle: 'Zmienić środowisko na: {environment}?',
      environmentConfirmProductionWarning:
        'UWAGA: przełączasz moduł na środowisko PRODUKCYJNE KSeF. Ta zmiana została odblokowana decyzją operatora. Od tej chwili wszystkie wystawiane faktury będą PRAWNIE WIĄŻĄCE i trafią do Krajowego Systemu e-Faktur na produkcji. Upewnij się, że certyfikat produkcyjny jest poprawnie skonfigurowany.',
      environmentConfirmGenericWarning:
        'Zmiana środowiska wpływa na to, dokąd trafiają wystawiane faktury i paragony. Upewnij się, że masz skonfigurowany właściwy certyfikat dla tego środowiska.',
      environmentUpdateSuccess: 'Zmieniono środowisko KSeF',
      environmentUpdateFailed: 'Nie udało się zmienić środowiska',
      environmentDefaultNote:
        'To jest środowisko DOMYŚLNE. Każdy token API (aplikacja) może mieć własne środowisko — ustawiane przy tworzeniu lub edycji tokena — a środowisko jest zamrażane na fakturze w momencie jej przyjęcia.',
      environmentSyncNote:
        'Dotyczy tylko wystawiania faktur i paragonów w tym module. Pobieranie faktur z KSeF (sync) ma osobne ustawienie w profilu firmy i pozostaje niezależne.',
      certificatesTitle: 'Certyfikaty',
      certificatesHint: 'Certyfikat kwalifikowany lub certyfikat KSeF potrzebny do podpisywania faktur w danym środowisku.',
      certificateMissing: 'Brak certyfikatu',
      certificateFingerprint: 'Odcisk',
      certificateExpiry: 'Ważny do',
      certificateExpired: 'Certyfikat wygasł',
      certificateExpiringSoon: 'Wygasa za {days} dni',
      uploadCertificate: 'Wgraj certyfikat',
      replaceCertificate: 'Zmień certyfikat',
      uploadCertificateTitle: 'Wgraj certyfikat — {environment}',
      certificateFile: 'Plik certyfikatu (.p12, .pfx, .pem, .crt)',
      certificatePassword: 'Hasło certyfikatu (opcjonalne dla .pem/.crt)',
      certificateFormatLabel: 'Wykryty format',
      certificateFormatP12: 'PKCS#12 (.p12/.pfx)',
      certificateFormatPem: 'PEM (.pem/.crt)',
      certificateWrongType: 'Nieprawidłowy typ pliku certyfikatu. Dozwolone: .p12, .pfx, .pem, .crt',
      certificateTooLarge: 'Plik certyfikatu jest za duży (max 10MB)',
      certificateUploadSuccess: 'Wgrano certyfikat',
      certificateUploadFailed: 'Nie udało się wgrać certyfikatu',
      deleteCertificate: 'Usuń certyfikat — {environment}',
      deleteCertificateConfirmTitle: 'Usunąć certyfikat — {environment}?',
      deleteCertificateConfirmMessage: 'Bez certyfikatu wysyłka faktur do KSeF w tym środowisku przestanie działać.',
      certificateDeleteSuccess: 'Usunięto certyfikat',
      certificateDeleteFailed: 'Nie udało się usunąć certyfikatu',
      numberingTitle: 'Numeracja faktur',
      autoInvoiceNum: 'Automatyczna numeracja faktur KSeF',
      autoInvoiceNumHint: 'Gdy włączone, numer faktury zostanie nadany automatycznie według wzorca poniżej.',
      prefixLabel: 'Prefiks numeru',
      prefixInvalid: 'Prefiks może zawierać wyłącznie litery i cyfry (1-10 znaków)',
      prefixPreview: 'Podgląd: {example}',
      countersTitle: 'Liczniki serii numeracji',
      countersHint:
        'Numeracja jest MIESIĘCZNA: każda seria (prefiks + środowisko) zaczyna każdy miesiąc od 0001, a każdy miesiąc to osobny wiersz poniżej. Domyślnie widać tylko najnowszy miesiąc każdej serii. Ustawianie licznika przydaje się przy migracji z innego systemu — np. gdy poprzedni system KSeF wystawił już faktury EBS/0001..0714, ustaw licznik na 714, aby kolejna faktura otrzymała numer 0715, a nie 0001 (KSeF odrzuca powtórzone numery jako duplikaty).',
      countersEmpty: 'Brak zainicjowanych liczników — pierwsza faktura w danej serii rozpocznie numerację od 0001.',
      counterPrefixColumn: 'Prefiks',
      counterEnvironmentColumn: 'Środowisko',
      counterPeriodColumn: 'Rok/miesiąc',
      counterLastSeqColumn: 'Ostatni numer',
      counterNextColumn: 'Przykład następnego',
      setCounter: 'Ustaw licznik',
      countersShowHistory: 'Pokaż historię ({count} starszych wierszy)',
      countersHideHistory: 'Ukryj historię',
      counterEnvironmentLabel: 'Środowisko',
      counterEnvironmentPlaceholder: 'Wybierz środowisko',
      setCounterTitle: 'Ustaw licznik serii numeracji',
      setCounterWarning:
        'Licznik można wyłącznie przesuwać do przodu (nowa wartość musi być ≥ obecnej) — to zabezpieczenie przed przypadkowym ponownym nadaniem już wystawionego numeru.',
      counterPrefixInputLabel: 'Prefiks serii',
      counterYearLabel: 'Rok',
      counterMonthLabel: 'Miesiąc',
      counterLastSeqLabel: 'Ostatni wystawiony numer',
      counterLastSeqHint: 'Numer ostatniej faktury już wystawionej w tej serii (np. przez poprzedni system) — kolejna faktura z Periscope otrzyma numer o 1 większy.',
      counterNextPreview: 'Przykład następnego numeru: {example}',
      setCounterSuccess: 'Ustawiono licznik',
      setCounterFailed: 'Nie udało się ustawić licznika',
      counterBackwardError: 'Licznik można tylko przesuwać do przodu — podana wartość jest mniejsza niż obecny stan licznika',
      sellerTitle: 'Dane sprzedawcy',
      sellerName: 'Nazwa sprzedawcy',
      sellerAddressLine1: 'Adres — linia 1',
      sellerAddressLine2: 'Adres — linia 2',
      sellerCountryCode: 'Kod kraju (ISO)',
      save: 'Zapisz ustawienia',
      saveSuccess: 'Zapisano ustawienia',
      saveFailed: 'Nie udało się zapisać ustawień',
      tokensTitle: 'Tokeny API',
      tokensHint: 'Tokeny do integracji zewnętrznych z API modułu fiskalizacji.',
      tokensEmpty: 'Brak tokenów API',
      tokenName: 'Nazwa',
      tokenPrefix: 'Prefiks',
      tokenNumberingPrefixes: 'Prefiksy numeracji',
      tokenEnvironment: 'Środowisko',
      tokenEnvironmentDefault: 'domyślne firmy',
      tokenEnvironmentLabel: 'Środowisko KSeF',
      tokenEnvironmentHelp:
        'Faktury z tego tokena będą wystawiane w wybranym środowisku, niezależnie od ustawienia firmy. Środowisko jest zamrażane na fakturze przy przyjęciu — zmiana działa tylko dla nowych faktur.',
      tokenEnvironmentProductionWarning:
        'PRODUKCJA: faktury wystawione tym tokenem będą prawnie wiążące i trafią do Krajowego Systemu e-Faktur.',
      tokenEnvNoCertificate:
        'Brak certyfikatu dla środowiska {environment} — submit z tego tokena będzie odrzucany (400), dopóki certyfikat nie zostanie wgrany.',
      tokenEnvExpiredCertificate:
        'Certyfikat dla środowiska {environment} wygasł — wysyłka do KSeF nie powiedzie się, dopóki nie wgrasz nowego.',
      tokenNumberingPrefixesDefault: 'domyślny firmy',
      tokenNumberingPrefixesLabel: 'Prefiksy numeracji',
      tokenNumberingPrefixesPlaceholder: 'np. EBS, YVS',
      tokenNumberingPrefixesHelp:
        'Lista dozwolonych prefiksów serii numeracji (rozdzielona przecinkami, 1-10 znaków alfanumerycznych każdy). Faktura wskazuje serię polem numberingPrefix; brak wartości → pierwszy prefiks z listy; pusta lista → domyślny prefiks firmy.',
      tokenPrefixesInvalid: 'Prefiksy mogą zawierać wyłącznie litery i cyfry (1-10 znaków każdy)',
      tokenLastUsed: 'Ostatnio użyty',
      tokenNeverUsed: 'Nigdy nieużyty',
      tokenCreated: 'Utworzono',
      addToken: 'Nowy token',
      addTokenTitle: 'Nowy token API',
      editToken: 'Edytuj token',
      editTokenTitle: 'Edytuj token API',
      tokenNameLabel: 'Nazwa tokenu',
      tokenNamePlaceholder: 'np. Integracja ERP',
      tokenNameRequired: 'Nazwa tokenu jest wymagana',
      createToken: 'Utwórz token',
      tokenCreateSuccess: 'Utworzono token',
      tokenCreateFailed: 'Nie udało się utworzyć tokenu',
      tokenUpdateSuccess: 'Zaktualizowano token',
      tokenUpdateFailed: 'Nie udało się zaktualizować tokenu',
      revoke: 'Unieważnij',
      revokeConfirmTitle: 'Unieważnić token „{name}”?',
      revokeConfirmMessage: 'Integracje korzystające z tego tokenu natychmiast przestaną działać.',
      revokeSuccess: 'Unieważniono token',
      revokeFailed: 'Nie udało się unieważnić tokenu',
      tokenRevealTitle: 'Token API utworzony',
      tokenRevealDescription: 'Nowy token dostępu do API modułu fiskalizacji.',
      tokenRevealWarning: 'Ten token zostanie pokazany tylko raz. Skopiuj go teraz i zapisz w bezpiecznym miejscu.',
      copyToken: 'Kopiuj token',
      tokenCopied: 'Skopiowano token',
      close: 'Rozumiem, zamknij',
      logoTitle: 'Logo paragonu',
      logoHint: 'Logo drukowane w nagłówku paragonów (e-paragon PDF).',
      logoSet: 'Logo ustawione',
      logoNotSet: 'Brak logo',
      logoPreviewUnavailable: 'Podgląd niedostępny po odświeżeniu strony — logo jest zapisane.',
      uploadLogo: 'Wgraj logo',
      replaceLogo: 'Zmień logo',
      removeLogo: 'Usuń logo',
      logoUploadSuccess: 'Wgrano logo',
      logoUploadFailed: 'Nie udało się wgrać logo',
      logoRemoveSuccess: 'Usunięto logo',
      logoRemoveFailed: 'Nie udało się usunąć logo',
      logoWrongType: 'Dozwolone formaty: PNG, JPEG',
      logoTooLarge: 'Plik logo jest za duży (max 2MB)',
    },
  },

  // Magazyn (WZ etap 1) — Spec: docs/superpowers/specs/2026-07-21-magazyn-wz-design.md
  magazyn: {
    title: 'Magazyn',
    tabs: {
      wz: 'Dokumenty WZ',
      pozycje: 'Pozycje',
    },
    deliveryMethods: {
      courier: 'Przesyłka kurierska',
      seller_delivery: 'Dostawa Sprzedawcy',
      pickup: 'Odbiór własny',
    },
    status: {
      issued: 'Wystawione',
      returned: 'Wróciło podpisane',
    },
    list: {
      title: 'Dokumenty WZ',
      searchPlaceholder: 'Szukaj po numerze, nabywcy lub kodzie…',
      statusAll: 'Wszystkie statusy',
      newWz: 'Nowe WZ',
      uploadSigned: 'Wgraj podpisany skan',
      clearFilters: 'Wyczyść filtry',
      columns: {
        number: 'Numer',
        issueDate: 'Data',
        buyer: 'Nabywca',
        deliveryMethod: 'Sposób dostawy',
        trackingNumber: 'Nr przesyłki (T&T)',
        status: 'Status',
        actions: 'Akcje',
      },
      downloadPdf: 'Pobierz PDF',
      downloadFailed: 'Nie udało się pobrać PDF',
      edit: 'Edytuj',
      delete: 'Usuń',
      deleteConfirmTitle: 'Usunąć dokument WZ {number}?',
      deleteConfirmDescription: 'Tej operacji nie można cofnąć.',
      deleteSuccess: 'Dokument WZ został usunięty',
      deleteFailed: 'Nie udało się usunąć dokumentu WZ',
      empty: {
        title: 'Brak dokumentów WZ',
        description: 'Nie znaleziono dokumentów pasujących do wybranych filtrów',
      },
    },
    editor: {
      title: 'Dokument WZ',
      titleNew: 'Nowy dokument WZ',
      titleEdit: 'Edycja dokumentu WZ {number}',
      seller: 'Wystawca',
      buyer: 'Nabywca',
      buyerPlaceholder: 'Wybierz kontrahenta…',
      issueDate: 'Data wystawienia',
      linesTitle: 'Pozycje dokumentu',
      addLine: 'Dodaj pozycję',
      removeLine: 'Usuń',
      ordinal: 'Lp.',
      itemFromCatalog: 'Z katalogu',
      itemManual: 'Wpisz ręcznie',
      columns: {
        item: 'Pozycja',
        code: 'Kod towaru',
        description: 'Opis / Uwagi',
        unit: 'J.m.',
        quantity: 'Ilość',
      },
      unitDefault: 'szt.',
      deliveryMethod: 'Sposób dostawy',
      trackingNumber: 'Nr przesyłki (T&T)',
      trackingNumberRequired: 'Numer przesyłki jest wymagany dla kuriera',
      notes: 'Uwagi',
      save: 'Zapisz',
      saveAndDownload: 'Zapisz i pobierz PDF',
      issueInvoice: 'Wystaw FV do WZ',
      fvSoon: 'Wkrótce — integracja z fiskalizacją',
      savedToast: 'Zapisano dokument {number}',
      saveFailed: 'Nie udało się zapisać dokumentu WZ',
      pdfDownloadFailed: 'Dokument {number} zapisano, ale nie udało się pobrać PDF',
      returnedBanner: 'Dokument wrócił podpisany — edycja tylko dla administratora',
      buyerLockedReturned: 'Nabywcy nie można zmienić po zwrocie dokumentu',
      quantityInvalid: 'Ilość musi być większa od zera',
      linesRequired: 'Dodaj przynajmniej jedną pozycję',
      codeRequired: 'Wybierz pozycję z katalogu lub wpisz kod',
    },
    items: {
      title: 'Pozycje',
      searchPlaceholder: 'Szukaj po kodzie, kolorze lub nazwie…',
      addItem: 'Dodaj pozycję',
      editItem: 'Edytuj pozycję',
      columns: {
        code: 'Kod',
        color: 'Kolor',
        name: 'Nazwa',
        project: 'Projekt',
        invoiceDescription: 'Opis na FV',
        unitPriceNet: 'Cena netto',
        actions: 'Akcje',
      },
      fields: {
        code: 'Kod',
        color: 'Kolor',
        name: 'Nazwa',
        project: 'Projekt',
        invoiceDescription: 'Opis na FV',
        unitPriceNet: 'Cena netto',
      },
      codeRequired: 'Kod jest wymagany',
      colorRequired: 'Kolor jest wymagany',
      noProject: 'Bez projektu',
      nameRequired: 'Nazwa jest wymagana',
      priceInvalid: 'Cena musi być liczbą ≥ 0',
      duplicateCode: 'Pozycja o takim kodzie już istnieje',
      deleteConfirmTitle: 'Usunąć pozycję {code}?',
      deleteConfirmDescription: 'Pozycja zniknie z podpowiedzi; istniejące WZ zachowają wpisane dane.',
      deleteSuccess: 'Pozycja została usunięta',
      deleteFailed: 'Nie udało się usunąć pozycji',
      saveSuccess: 'Pozycja została zapisana',
      saveFailed: 'Nie udało się zapisać pozycji',
      empty: {
        title: 'Brak pozycji w katalogu',
        description: 'Dodaj pierwszą pozycję, aby korzystać z podpowiedzi na WZ',
      },
    },
    upload: {
      title: 'Wgraj podpisany skan',
      dropzoneLabel: 'Przeciągnij plik tutaj lub kliknij, aby wybrać',
      dropzoneHint: 'PDF, JPG lub PNG',
      submit: 'Wgraj',
      success: 'Podpięto do {wzNumber}',
      attachmentCreated: 'Skan został dołączony do dokumentu',
      needsSelectionTitle: 'Nie udało się dopasować dokumentu automatycznie',
      needsSelectionDescription: 'Wybierz dokument WZ, do którego należy dołączyć skan',
      searchPlaceholder: 'Szukaj po numerze WZ…',
      confirm: 'Dołącz',
      error: 'Nie udało się wgrać skanu',
      foreignToken: 'Kod QR na skanie należy do innego dokumentu, nie do WZ tej firmy',
      wrongType: 'Dozwolone formaty: PDF, JPG, PNG',
    },
  },

  mileage: {
    places: {
      name: 'Nazwa',
      city: 'Miasto',
      distanceMin: 'Odległość (km) musi wynosić co najmniej 1',
      monthlyMinVisits: 'Min. wizyt/mies.',
      monthlyMaxVisits: 'Maks. wizyt/mies.',
      visitsRange: '{label} musi być z zakresu od 0 do 20',
      routesFound: 'Znaleziono {count} wariant(ów) trasy',
      computeFailed: 'Nie udało się wyznaczyć tras',
      editAria: 'Edytuj miejsce',
      deleteAria: 'Usuń miejsce',
    },
    title: 'Ewidencja przebiegu',
    titleWithCompany: 'Ewidencja przebiegu: {companyName}',
    subtitle: 'Zarządzaj ewidencją przebiegu pojazdów dla celów VAT',
    tabs: {
      vehicles: 'Pojazdy',
      routes: 'Trasy standardowe',
      monthly: 'Ewidencja miesięczna',
    },
    vehicles: {
      title: 'Pojazdy firmowe',
      add: 'Dodaj pojazd',
      editTitle: 'Edytuj pojazd',
      displayName: 'Nazwa wyświetlana',
      registrationNumber: 'Numer rejestracyjny',
      initialOdometer: 'Przebieg początkowy (km)',
      initialOdometerDate: 'Data przebiegu',
      isActive: 'Aktywny',
      notes: 'Uwagi',
      drivers: 'Kierowcy',
      addDriver: 'Dodaj kierowcę',
      driverName: 'Imię i nazwisko',
      isEmployee: 'Pracownik',
      isDefault: 'Domyślny',
      noVehicles: 'Brak dodanych pojazdów',
    },
    routes: {
      enterAddressesFirst: 'Najpierw wprowadź adresy Od i Do',
      calculateFailed: 'Nie udało się obliczyć odległości',
      routesFound: 'Znaleziono {count} tras(y). {km} km w jedną stronę.',
      noPlacesFound: 'Nie znaleziono miejsc',
      searchFailed: 'Nie udało się wyszukać miejsca',
      addressFound: 'Znaleziono adres: {address}',
      title: 'Trasy standardowe',
      add: 'Dodaj trasę',
      editTitle: 'Edytuj trasę',
      routeFrom: 'Z',
      routeTo: 'Do',
      distanceKm: 'Odległość (km)',
      defaultPurpose: 'Domyślny cel',
      isRoundTrip: 'W obie strony',
      calculateDistance: 'Oblicz odległość',
      noRoutes: 'Brak zdefiniowanych tras standardowych',
    },
    records: {
      odometerStart: 'Przebieg początkowy',
      odometerEnd: 'Przebieg końcowy',
      totalKm: 'Łącznie km',
      entriesKm: 'Wpisy km',
      delta: 'Różnica',
      status: {
        draft: 'Szkic',
        reconciled: 'Uzgodniony',
        finalized: 'Zatwierdzony',
      },
    },
    entries: {
      add: 'Dodaj wpis',
      edit: 'Edytuj wpis',
      tripDate: 'Data',
      routeDescription: 'Trasa',
      purpose: 'Cel wyjazdu',
      distanceKm: 'km',
      driverName: 'Kierowca',
      isRoundTrip: 'W obie strony',
    },
    isByCar: 'Samochodem',
  },

  employees: {
    title: 'Pracownicy',
    titleWithCompany: 'Pracownicy: {companyName}',
    subtitle: 'Zarządzaj pracownikami, stawkami, godzinami i umowami w tej firmie',
    addEmployee: 'Dodaj pracownika',
    noEmployees: 'Nie znaleziono pracowników',
    noEmployeesDescription: 'Brak pracowników pasujących do filtrów',
    type: {
      mandate_contract: 'Umowa zlecenie',
      mandate_contract_student: 'Umowa zlecenie (student)',
      employment_contract: 'Umowa o pracę',
      contractor: 'B2B / Kontraktor',
      powolanie: 'Członek zarządu (powołanie)',
      dzielo: 'Umowa o dzieło',
      label: 'Typ',
      notSet: 'Nie ustawiono',
    },
    myDataLock: {
      badge: 'Odsłanianie danych zablokowane (zbyt wiele prób PESEL)',
      unlock: 'Odblokuj',
      unlocked: 'Odsłanianie danych odblokowane',
    },
    appAccount: 'Konto w aplikacji',
    status: {
      active: 'Aktywny',
      expiring_soon: 'Wygasa wkrótce',
      expired: 'Wygasł',
      terminated: 'Rozwiązany',
      label: 'Status',
      all: 'Wszystkie',
    },
    rateType: {
      flat_monthly: 'Stała miesięczna',
      hourly: 'Godzinowa',
      label: 'Typ stawki',
    },
    identity: {
      tab: 'Dane osobowe',
      firstName: 'Imię',
      lastName: 'Nazwisko',
      pesel: 'PESEL',
      peselHelp: 'Data urodzenia i płeć są wyliczane z numeru PESEL',
      dateOfBirth: 'Data urodzenia',
      sex: 'Płeć',
      sexValue: { male: 'Mężczyzna', female: 'Kobieta' },
      derivedFromPesel: 'z numeru PESEL',
      peselOverrodeDateOfBirth:
        'Data urodzenia została zapisana z numeru PESEL ({derived}) zamiast wpisanej ({submitted})',
      idDocumentType: 'Rodzaj dokumentu',
      idDocumentNumber: 'Numer dokumentu',
      taxOffice: 'Urząd Skarbowy',
      nfzBranch: 'Oddział NFZ',
      privateEmail: 'Email prywatny',
      privatePhone: 'Telefon prywatny',
      address: 'Adres',
      street: 'Ulica',
      postalCode: 'Kod pocztowy',
      city: 'Miasto',
      country: 'Kraj',
      privateIban: 'Prywatny IBAN',
      privateIbanHelp: 'Używany do zwrotu wydatków poniesionych prywatnie na rzecz firmy',
      notes: 'Notatki',
      idDocument: { dowod: 'Dowód osobisty', passport: 'Paszport' },
    },
    employment: {
      tab: 'Zatrudnienie',
      type: 'Typ zatrudnienia',
      contractStartDate: 'Początek umowy',
      contractExpiryDate: 'Koniec umowy',
      terminate: 'Rozwiąż umowę',
      terminateConfirm: 'Rozwiązać umowę z tym pracownikiem? Zostanie automatycznie zarchiwizowany.',
      archive: 'Archiwizuj',
      unarchive: 'Przywróć z archiwum',
      contractor: {
        title: 'Kontraktor B2B',
        searchPlaceholder: 'Wpisz NIP kontraktora lub wybierz z listy',
        createFromBir: 'Utwórz z BIR ({nip})',
      },
      studentCard: {
        title: 'Legitymacja studencka',
        upload: 'Wgraj legitymację',
        replace: 'Wymień legitymację',
        delete: 'Usuń legitymację',
        download: 'Pobierz',
        expiryDate: 'Termin ważności',
      },
    },
    rates: {
      tab: 'Stawki',
      addRate: 'Dodaj nową stawkę',
      addRateModal: 'Dodaj stawkę',
      editRate: 'Edytuj stawkę',
      editRateModal: 'Edytuj stawkę',
      amount: 'Kwota (PLN)',
      validFrom: 'Obowiązuje od',
      validTo: 'Obowiązuje do',
      current: 'Aktualna',
      noRates: 'Brak ustawionych stawek',
      delete: 'Usuń stawkę',
      deleteConfirm: 'Usunąć tę stawkę?',
    },
    hours: {
      tab: 'Godziny',
      year: 'Rok',
      month: 'Miesiąc',
      hours: 'Godziny',
      monthlyCost: 'Koszt miesięczny',
      months: {
        1: 'Styczeń', 2: 'Luty', 3: 'Marzec', 4: 'Kwiecień',
        5: 'Maj', 6: 'Czerwiec', 7: 'Lipiec', 8: 'Sierpień',
        9: 'Wrzesień', 10: 'Październik', 11: 'Listopad', 12: 'Grudzień',
      },
      hourlyOnlyHint: 'Godziny miesięczne mają znaczenie tylko gdy aktualna stawka jest godzinowa',
    },
    documents: {
      tab: 'Dokumenty',
      upload: 'Wgraj dokument',
      type: 'Typ dokumentu',
      customLabel: 'Własna etykieta',
      complianceHint: 'Ten typ jest śledzony w Terminach i zgodności — po wgraniu wymóg zostanie oznaczony jako spełniony.',
      types: {
        umowa: 'Umowa',
        aneks: 'Aneks do umowy',
        certyfikat: 'Certyfikat / uprawnienia',
        zaswiadczenie: 'Zaświadczenie',
        other: 'Inne',
      },
      label: 'Etykieta',
      fileName: 'Nazwa pliku',
      uploadedAt: 'Wgrano',
      expiry: 'Wygasa',
      download: 'Pobierz',
      delete: 'Usuń',
      deleteConfirm: 'Usunąć ten dokument?',
      expiryWarning: 'Wygasa za {days} dni',
      expired: 'Wygasł',
      signedPublicationBadge: 'Podpisany dokument',
      signedConfirmed: 'Potwierdzony',
      signedAwaitingConfirmation: 'Do potwierdzenia',
      signatureDetails: 'Szczegóły podpisów',
    },
    costReport: {
      title: 'Raport kosztów',
      titleWithMonth: 'Raport kosztów: {month} {year}',
      pickMonth: 'Miesiąc',
      pickYear: 'Rok',
      employee: 'Pracownik',
      monthlyCost: 'Koszt miesięczny',
      totalByType: 'Razem wg typu',
      grandTotal: 'Suma',
      noActiveRate: 'Brak aktywnej stawki',
      missingHours: 'Brak wpisanych godzin',
      exportCsv: 'Eksport CSV',
    },
    payroll: {
      tab: 'Rozliczenie',
      gross: 'Brutto',
      net: 'Netto',
      employerCost: 'Koszt pracodawcy',
      noRate: 'brak stawki',
      noType: 'brak typu umowy',
      cardTitle: 'Rozliczenie miesięczne',
      cardSubtitle: '{month} {year} — wg aktualnej stawki i formy zatrudnienia',
      rate: 'Stawka',
      hours: 'Godziny',
      employeeContributions: 'Składki pracownika (ZUS)',
      pension: 'Emerytalna (9,76%)',
      disability: 'Rentowa (1,50%)',
      sickness: 'Chorobowa (2,45%)',
      contributionsTotal: 'Razem składki',
      health: 'Składka zdrowotna (9%)',
      kup: 'Koszty uzyskania przychodu',
      taxBase: 'Podstawa opodatkowania',
      pitAdvance: 'Zaliczka PIT',
      ppkEmployee: 'PPK (pracownik)',
      employerSection: 'Koszt pracodawcy',
      employerContributionsSection: 'Składki pracodawcy (ZUS)',
      erPension: 'Emerytalna (9,76%)',
      erDisability: 'Rentowa (6,50%)',
      erAccident: 'Wypadkowa',
      erFpFs: 'FP i FS (2,45%)',
      erFgsp: 'FGŚP (0,10%)',
      erPpk: 'PPK (pracodawca)',
      employerContributionsTotal: 'Razem składki pracodawcy',
      erTotal: 'Koszt całkowity pracodawcy',
      assumptions: 'Założenia',
      notAvailable: 'Rozliczenie niedostępne: {reason}',
      loadFailed: 'Nie udało się obliczyć rozliczenia',
    },
    filters: {
      search: 'Szukaj po imieniu, PESEL, email',
      includeArchived: 'Pokaż zarchiwizowanych',
    },
    validation: {
      firstNameRequired: 'Imię jest wymagane',
      lastNameRequired: 'Nazwisko jest wymagane',
      typeRequired: 'Typ zatrudnienia jest wymagany',
      contractExpiryRequired: 'Data wygaśnięcia umowy jest wymagana dla umów zlecenie',
      contractorRequired: 'Należy wybrać kontraktora dla typu B2B',
      studentCardRequired: 'Legitymacja studencka musi być wgrana dla typu studenckiego',
      pesel: {
        length: 'Nieprawidłowy PESEL (musi mieć 11 cyfr)',
        digits: 'Nieprawidłowy PESEL (dozwolone są wyłącznie cyfry)',
        checksum: 'Nieprawidłowy PESEL (błędna suma kontrolna)',
        date: 'Nieprawidłowy PESEL (nieprawidłowa data urodzenia)',
      },
      ibanInvalid: 'Nieprawidłowy IBAN',
      datesInOrder: 'Data początku musi być nie późniejsza niż data wygaśnięcia',
      fileRequired: 'Plik jest wymagany',
      required: '{label} jest wymagane',
    },
  },

  theme: {
    toggle: 'Przełącz motyw',
    light: 'Jasny',
    dark: 'Ciemny',
    system: 'Systemowy',
  },

  companies: {
    loadFailed: 'Nie udało się wczytać danych firmy',
    saved: 'Zapisano firmę',
    saveFailed: 'Nie udało się zapisać firmy',
    bankAccount: {
      ibanRequired: 'Rachunek {index}: IBAN jest wymagany',
      ibanInvalid: 'Rachunek {index}: {error}',
      currencyRequired: 'Rachunek {index}: waluta jest wymagana',
    },
    title: 'Firmy',
    subtitle: 'Zarządzaj firmami w systemie',
    addCompany: 'Dodaj firmę',
    columns: {
      name: 'Nazwa',
      nip: 'NIP',
      invoices: 'Faktury',
      contractors: 'Kontrahenci',
    },
    noCompanies: 'Nie znaleziono firm',
    fetchFromBir: 'Pobierz z BIR',
    fetchFromBirSuccess: 'Dane firmy pobrane z BIR',
    fetchFromBirFailed: 'Nie znaleziono firmy w BIR',
    nip: 'NIP',
    name: 'Nazwa',
    street: 'Ulica',
    postalCode: 'Kod pocztowy',
    city: 'Miasto',
    country: 'Kraj',
    createSuccess: 'Firma utworzona',
    duplicateNip: 'Firma z tym NIP już istnieje',
    deleteCompany: 'Usuń firmę',
    deleteWarningTitle: 'Usunąć firmę {nip}?',
    deleteWarningBody:
      'Tej operacji nie można cofnąć. Dotyczy to wszystkich faktur, kontrahentów, tagów, pracowników, pojazdów i plików powiązanych z tą firmą.',
    deleteModeUnassign: 'Przenieś faktury do Innych, resztę usuń',
    deleteModePurge: 'Usuń wszystkie dane (nieodwracalne)',
    confirmNipLabel: 'Przepisz NIP aby potwierdzić',
    confirmNipPlaceholder: 'Wpisz NIP',
    deleted:
      'Usunięto firmę {nip}: {invoices} faktur, {contractors} kontrahentów, {tags} tagów, {employees} pracowników, {vehicles} pojazdów, CRM: {crmAccounts} klientów i {crmLeads} leadów',
    deleteS3Warning: 'Niektórych plików nie udało się w pełni wyczyścić — wymagają ręcznego przeglądu',
    form: {
      bdoNumber: 'Numer BDO',
      krsNumber: 'Numer KRS',
      contactEmail: 'E-mail firmowy',
      website: 'Strona WWW',
    },
    emailDomains: {
      label: 'Domeny e-mail pracowników',
      hint: 'Użytkownicy Entra z tych domen automatycznie dostają kartę pracownika',
      placeholder: 'np. fss.cc — Enter dodaje',
      invalid: 'Nieprawidłowa domena: {domain}',
    },
    logo: {
      label: 'Logo firmy',
      hint: 'Widoczne w panelu bocznym obok nazwy firmy',
      constraints: 'PNG, JPEG lub WebP — maks. 2 MB (SVG nie jest obsługiwany)',
      dropzone: 'Przeciągnij obraz tutaj lub kliknij, aby wybrać plik',
      choose: 'Wybierz plik',
      replace: 'Zmień',
      remove: 'Usuń',
      alt: 'Logo firmy {company}',
      uploaded: 'Logo zaktualizowane',
      removed: 'Logo usunięte',
      uploadFailed: 'Nie udało się wgrać logo',
      removeFailed: 'Nie udało się usunąć logo',
      tooLarge: 'Plik jest za duży (maks. 2 MB)',
      wrongType: 'Nieobsługiwany format — użyj PNG, JPEG lub WebP',
    },
    representation: {
      label: 'Reprezentacja firmy',
      hint: 'Osoby podpisujące umowy w imieniu firmy — wymagane przy generowaniu dokumentów umów',
      mode: 'Tryb reprezentacji',
      modeNotSet: 'Nieskonfigurowana',
      modeOnePerson: 'Jednoosobowa',
      modeTwoPerson: 'Dwuosobowa',
      add: 'Dodaj reprezentanta',
      empty: 'Brak skonfigurowanych reprezentantów',
      person: 'Reprezentant {index}',
      fullName: 'Imię i nazwisko',
      fullNamePlaceholder: 'np. Jan Kowalski',
      titleLabel: 'Funkcja',
      titlePlaceholder: 'np. Prezes Zarządu',
      fullNameRequired: 'Reprezentant {index}: imię i nazwisko jest wymagane',
      titleRequired: 'Reprezentant {index}: funkcja jest wymagana',
      twoPersonMin: 'Reprezentacja dwuosobowa wymaga co najmniej 2 reprezentantów',
    },
  },

  documents: {
    title: 'Dokumenty',
    templates: {
      title: 'Szablony',
      newTemplate: 'Nowy szablon',
      name: 'Nazwa',
      namePlaceholder: 'Nazwa szablonu',
      description: 'Opis',
      descriptionPlaceholder: 'Opcjonalny opis szablonu',
      category: 'Kategoria',
      categories: {
        employee: 'Pracownicze',
        b2b: 'B2B',
        contractor: 'Kontrahenta',
      },
      visibility: 'Widoczność',
      visibilities: {
        public: 'Publiczny',
        private: 'Prywatny (HR)',
      },
      acceptance: 'Akceptacja',
      acceptances: {
        none: 'Brak',
        electronic: 'Elektroniczna',
        physical: 'Podpis',
      },
      audience: 'Odbiorcy',
      audiences: {
        all: 'Wszyscy',
        uop: 'Umowa o pracę',
        uz: 'Umowa zlecenie',
        b2b: 'B2B',
      },
      draft: 'szkic',
      version: 'Wersja',
      updatedAt: 'Zaktualizowano',
      archived: 'Zarchiwizowany',
      archive: 'Archiwizuj',
      unarchive: 'Przywróć',
      archiveConfirm: 'Zarchiwizować szablon {name}?',
      archiveSuccess: 'Szablon zarchiwizowany',
      unarchiveSuccess: 'Szablon przywrócony',
      archiveError: 'Nie udało się zaktualizować szablonu',
      created: 'Szablon utworzony',
      updated: 'Zapisano',
      createError: 'Nie udało się utworzyć szablonu',
      loadError: 'Nie udało się wczytać szablonów',
      nameRequired: 'Nazwa jest wymagana',
      empty: 'Brak szablonów',
      templateKind: 'Typ szablonu',
      templateKinds: {
        standard: 'Standardowy',
        umowa_zlecenie: 'Umowa zlecenie',
      },
      starterHint: 'Albo zacznij od wbudowanego wzorca:',
      starterUmowa: 'Ze wzorca: Umowa zlecenie (student)',
      starterCreated: 'Szablon ze wzorca utworzony i opublikowany',
      starterError: 'Nie udało się utworzyć szablonu ze wzorca',
    },
    editor: {
      emptyDraft: 'Nie można opublikować pustego szablonu',
      back: 'Wróć do listy',
      settings: 'Ustawienia',
      preview: 'Podgląd PDF',
      publish: 'Publikuj wersję',
      publishConfirm: 'Opublikować wersję v{version}? Treść wersji będzie niezmienna.',
      published: 'Wersja opublikowana',
      history: 'Historia wersji',
      noVersions: 'Brak opublikowanych wersji',
      variables: 'Zmienne',
      groups: {
        firma: 'Firma',
        pracownik: 'Pracownik',
        kontrahent: 'Kontrahent',
        dokument: 'Dokument',
        wystawca: 'Wystawca (zalogowany)',
        umowa: 'Umowa',
        plec: 'Odmiana wg płci',
      },
      vars: {
        firma: {
          nazwa: 'Nazwa firmy',
          nip: 'NIP firmy',
          bdo: 'Numer BDO',
          krs: 'Numer KRS',
          email: 'E-mail firmy',
          www: 'Strona WWW',
          ulica: 'Ulica',
          kod: 'Kod pocztowy',
          miasto: 'Miasto',
        },
        pracownik: {
          imie: 'Imię',
          nazwisko: 'Nazwisko',
          ulica: 'Ulica',
          kod: 'Kod pocztowy',
          miasto: 'Miasto',
          adres: 'Pełny adres',
          pesel: 'PESEL',
          stanowisko: 'Stanowisko',
          typ_umowy: 'Typ umowy',
          data_zatrudnienia: 'Data zatrudnienia',
          data_urodzenia: 'Data urodzenia',
          urzad_skarbowy: 'Urząd skarbowy',
          rachunek_bankowy: 'Rachunek bankowy (IBAN)',
        },
        kontrahent: {
          nazwa: 'Nazwa kontrahenta',
          nip: 'NIP kontrahenta',
          adres: 'Adres kontrahenta',
        },
        dokument: {
          data: 'Data dokumentu',
          wersja: 'Wersja szablonu',
          numer: 'Numer dokumentu',
        },
        wystawca: {
          imie_nazwisko: 'Imię i nazwisko',
          email: 'E-mail',
        },
        umowa: {
          data_zawarcia: 'Data zawarcia',
          data_od: 'Początek umowy',
          data_do: 'Koniec umowy',
          stawka: 'Stawka godzinowa',
          reprezentacja: 'Reprezentacja firmy',
        },
      },
      insertHint: 'Kliknij, aby wstawić',
      plecHint:
        'Forma dobierana wg płci pracownika (z PESEL). Wstaw i dopasuj końcówki: {{plec:męska/żeńska}}',
      saving: 'Zapisywanie…',
      saved: 'Zapisano',
      deadlineDays: 'Domyślny termin (dni)',
      logo: 'Logo firmy w nagłówku',
      unknownVars: 'Nieznane zmienne',
      pagebreakHint: 'Wstaw [[pagebreak]] w osobnej linii, aby wymusić nową stronę',
      modeWysiwyg: 'Edytor',
      modeRaw: 'Markdown',
      pagebreakLabel: 'Podział strony',
      wysiwygLoadError:
        'Nie udało się załadować edytora wizualnego. Odśwież stronę lub użyj trybu Markdown.',
      toolbar: {
        undo: 'Cofnij',
        redo: 'Ponów',
        h1: 'Nagłówek 1',
        h2: 'Nagłówek 2',
        h3: 'Nagłówek 3',
        paragraph: 'Zwykły tekst',
        bold: 'Pogrubienie',
        italic: 'Kursywa',
        strike: 'Przekreślenie',
        code: 'Kod',
        bulletList: 'Lista punktowana',
        orderedList: 'Lista numerowana',
        quote: 'Cytat',
        table: 'Tabela',
        hr: 'Linia pozioma',
        pagebreak: 'Podział strony',
        addRow: 'Dodaj wiersz (w tabeli)',
        addColumn: 'Dodaj kolumnę (w tabeli)',
        deleteRow: 'Usuń wiersz',
        deleteColumn: 'Usuń kolumnę',
        deleteTable: 'Usuń tabelę',
      },
    },
    publications: {
      title: 'Publikacje',
      publish: 'Publikuj…',
      publishDialogTitle: 'Publikuj dokument',
      kind: 'Rodzaj',
      kinds: {
        campaign: 'Kampania',
        individual: 'Indywidualny',
      },
      audience: 'Odbiorcy',
      audienceRequired: 'Wybierz odbiorców',
      employee: 'Pracownik',
      employeeRequired: 'Wybierz pracownika',
      employeeSearchPlaceholder: 'Szukaj pracownika…',
      noEmployees: 'Brak pracowników',
      deadline: 'Termin',
      noDeadline: 'Bez terminu',
      acceptanceMode: 'Akceptacja',
      physicalCampaignHint: 'Podpis papierowy wymaga publikacji indywidualnej',
      submit: 'Publikuj',
      published: 'Opublikowano {number}',
      publishError: 'Nie udało się opublikować dokumentu',
      number: 'Numer',
      recipient: 'Odbiorca',
      progress: 'Akceptacje',
      publishedAt: 'Opublikowano',
      empty: 'Brak publikacji',
      archived: 'Zarchiwizowana',
      archive: 'Archiwizuj',
      archiveConfirm: 'Zarchiwizować publikację {number}? Nie będzie już przypominana pracownikom.',
      archiveSuccess: 'Publikacja zarchiwizowana',
      archiveError: 'Nie udało się zarchiwizować publikacji',
      loadError: 'Nie udało się wczytać publikacji',
      detailTitle: 'Publikacja',
      download: 'Pobierz PDF',
      downloadError: 'Nie udało się pobrać dokumentu',
      employeeCol: 'Pracownik',
      status: 'Status',
      statuses: {
        pending: 'Oczekuje',
        returned: 'Zwrócono',
        accepted: 'Zaakceptowano',
        signed: 'Podpisano',
      },
      method: 'Metoda',
      methods: {
        electronic: 'Elektroniczna',
        physical: 'Podpis',
      },
      acceptedAt: 'Data',
      noAcceptances: 'Brak wymaganych akceptacji',
      markSigned: 'Oznacz jako podpisany (skan)',
      markSignedTitle: 'Wgraj podpisany skan',
      markSignedFile: 'Plik skanu',
      markSignedSuccess: 'Zapisano podpisany dokument',
      markSignedError: 'Nie udało się zapisać skanu',
      attachRevision: 'Wgraj nowszą podpisaną wersję',
      attachRevisionTitle: 'Nowsza wersja podpisanego dokumentu',
      attachRevisionFile: 'Plik PDF z dodatkowym podpisem',
      attachRevisionHint: 'Przyjmowana jest wyłącznie nowsza wersja TEGO SAMEGO pliku (ten sam dokument z dodatkowym podpisem osoby, na którą dokument czeka — np. PDF kontrasygnowany przez reprezentanta). Inny skan lub inny plik wgraj przy kontrasygnacie przyciskiem „Podpisz”.',
      attachRevisionSuccess: 'Zapisano nowszą wersję podpisanego dokumentu',
      attachRevisionError: 'Nie udało się zapisać nowszej wersji',
      attachRevisionCheck: 'Sprawdź plik',
      attachRevisionApply: 'Zastosuj tę wersję',
      attachRevisionAdds: 'Ten plik dodaje poniższe podpisy. Zastosuj tylko wtedy, gdy to osoba, na którą dokument czeka — zapisany podpisany plik zostanie zastąpiony tą wersją, a jej podpisy podpiszą pasujące wiersze kontrasygnat.',
      attachRevisionPreviewError: 'Nie udało się sprawdzić pliku',
      confirmReturn: 'Potwierdź podpis',
      confirmReturnSuccess: 'Zwrot potwierdzony',
      returnedBadge: '{count} do potwierdzenia',
      downloadReturned: 'Pobierz podpisany skan',
      confirmReturnError: 'Nie udało się potwierdzić zwrotu',
      contract: {
        section: 'Dane umowy',
        individualHint: 'Umowa jest zawsze publikacją indywidualną',
        conclusionDate: 'Data zawarcia',
        dateFrom: 'Okres od',
        dateTo: 'Okres do',
        hourlyRate: 'Stawka godzinowa brutto (zł/h)',
        representation: 'Reprezentacja firmy',
        modes: {
          one_person: 'Reprezentacja jednoosobowa',
          two_person: 'Reprezentacja dwuosobowa',
        },
        selectExactly: 'wybierz {count} os.',
        orderHint: 'Kolejność wyboru wyznacza kolejność podpisywania (1. podpisuje pierwszy)',
        notConfigured:
          'Reprezentacja firmy nie jest skonfigurowana — ustaw tryb i reprezentantów w ustawieniach firmy przed publikacją tej umowy',
        loadFailed: 'Nie udało się pobrać reprezentacji firmy — publikacja jest zablokowana do czasu wczytania',
        retry: 'Ponów',
        writeRate: 'Zapisz stawkę w kartotece pracownika',
        datesRequired: 'Data zawarcia i okres umowy są wymagane',
        dateOrder: 'Początek okresu nie może być po jego końcu',
        rateRequired: 'Podaj prawidłową stawkę godzinową',
        rateFormat: 'Stawka musi być większa od 0 i mieć maks. 2 miejsca po przecinku',
        rateWarning: 'Opublikowano, ale stawki nie zapisano w kartotece: {message}',
      },
      countersigns: {
        title: 'Podpisy reprezentacji',
        fullySigned: 'W pełni podpisana',
        statuses: {
          waiting: 'Oczekuje',
          pending: 'Do podpisu',
          signed: 'Podpisany',
        },
        sign: 'Podpisz',
        signTitle: 'Podpis reprezentanta — {name}',
        signFile: 'Podpisany plik (opcjonalnie)',
        signFileHint:
          'Załącz PDF podpisany cyfrowo lub skan; zostaw puste, gdy podpisano wyłącznie na papierze',
        signSuccess: 'Podpis zarejestrowany',
        signError: 'Nie udało się zarejestrować podpisu',
        downloadFile: 'Pobierz podpisany plik',
      },
    },
  },

  errors: {
    loadingFailed: 'Nie udało się wczytać danych',
    saveFailed: 'Nie udało się zapisać',
    deleteFailed: 'Nie udało się usunąć',
    accessDenied: 'Brak dostępu',
    notFound: 'Nie znaleziono',
    pdfFailed: 'Nie udało się wygenerować PDF',
    sessionExpired: 'Sesja wygasła',
    sessionExpiredMessage: 'Twoja sesja wygasła. Zaloguj się ponownie, aby kontynuować.',
    signInAgain: 'Zaloguj się ponownie',
    signInFailed: 'Nie udało się rozpocząć logowania. Odśwież stronę i spróbuj ponownie.',
    reloadPage: 'Odśwież stronę',
  },

  auth: {
    tabMicrosoft: 'Microsoft',
    tabPassword: 'Hasło',
    email: 'E-mail',
    password: 'Hasło',
    signIn: 'Zaloguj się',
    signInWithMicrosoft: 'Zaloguj się przez Microsoft',
    microsoftHint: 'Dostęp wymaga członkostwa w autoryzowanej grupie',
    invalidCredentials: 'Nieprawidłowy e-mail lub hasło',
    tooManyAttempts: 'Zbyt wiele prób. Spróbuj ponownie za 15 minut.',
    loginFailed: 'Logowanie nie powiodło się. Spróbuj ponownie.',
    turnstileRequired: 'Poczekaj na weryfikację antybotową',
    turnstileFailed: 'Weryfikacja antybotowa nie powiodła się. Spróbuj ponownie.',
    verificationUnavailable:
      'Weryfikacja logowania jest chwilowo niedostępna. Spróbuj ponownie za chwilę.',
    changePasswordTitle: 'Ustaw nowe hasło',
    changePasswordSubtitle: 'Logujesz się hasłem tymczasowym. Ustaw nowe hasło, aby kontynuować.',
    currentPassword: 'Obecne hasło',
    newPassword: 'Nowe hasło',
    confirmPassword: 'Potwierdź nowe hasło',
    passwordMinLengthHint: 'Co najmniej {min} znaków',
    passwordTooShort: 'Hasło musi mieć co najmniej {min} znaków',
    passwordMismatch: 'Hasła nie są zgodne',
    wrongCurrentPassword: 'Obecne hasło jest nieprawidłowe',
    changePasswordFailed: 'Nie udało się zmienić hasła. Spróbuj ponownie.',
    changePasswordSubmit: 'Zmień hasło i zaloguj się',
    // Sonar scope instrument chatter (login background scene)
    scopeStation: 'GŁĘBOKOŚĆ PERYSKOPOWA',
    scopeDepth: 'GŁ. {m} M',
    scopeBearing: 'NAMIAR {deg}°',
    scopeContact: 'KONTAKT ZIDENTYFIKOWANY · NAMIAR 047',
    scopeRetry: 'POWTÓRZ NAMIAR',
  },

  profile: {
    apiToken: {
      menuItem: 'Token API',
      title: 'Token API',
      description:
        'Osobisty token do wysyłania faktur przez API (np. skrót iOS). Wysyłaj go do POST /invoice/upload w nagłówku „Authorization: Bearer …” lub „X-Api-Key”.',
      noToken: 'Nie masz jeszcze tokenu API.',
      tokenLabel: 'Token',
      created: 'Utworzono',
      lastUsed: 'Ostatnio użyty',
      neverUsed: 'nigdy',
      generate: 'Wygeneruj token',
      rotate: 'Wymień token',
      rotateConfirm:
        'Wymiana zastępuje obecny token — wszystko, co nadal używa starego, przestanie działać. Kontynuować?',
      revoke: 'Unieważnij token',
      revokeConfirm: 'Unieważnić token? Wszystko, co go używa, przestanie działać.',
      newTokenTitle: 'Twój nowy token',
      newTokenWarning: 'Skopiuj go teraz — ze względów bezpieczeństwa nie zostanie ponownie wyświetlony.',
      copy: 'Kopiuj',
      copied: 'Token skopiowany do schowka',
      loadFailed: 'Nie udało się pobrać statusu tokenu API',
      generateFailed: 'Nie udało się wygenerować tokenu API',
      revokeFailed: 'Nie udało się unieważnić tokenu API',
      revoked: 'Token API unieważniony',
    },
  },

  apiApps: {
    title: 'Aplikacje API',
    description:
      'Globalne tokeny dla zewnętrznych aplikacji (pap_…). Dostęp tylko do danych rejestrów — nigdy do faktur i paragonów.',
    addApp: 'Dodaj aplikację',
    name: 'Nazwa',
    descriptionLabel: 'Opis',
    scopes: 'Uprawnienia',
    scopeCompanyPl: 'Biała Lista — polski NIP (company:lookup:pl)',
    scopeCompanyEu: 'VIES — unijny VAT (company:lookup:eu)',
    scopeCrmLeads: 'CRM — przyjmowanie leadów (crm:leads:write)',
    companyNip: 'Firma',
    companyNipHint:
      'Wymagane dla uprawnienia CRM — leady trafią do CRM wybranej firmy. „Token globalny” = tylko lookupy.',
    companyGlobal: 'Token globalny (tylko lookupy)',
    companyOutOfList: '(spoza listy)',
    companyNipRequired: 'Uprawnienie CRM wymaga wybrania firmy',
    expiresAt: 'Wygasa',
    neverExpires: 'Bezterminowo',
    expired: 'Wygasł',
    lastUsed: 'Ostatnio użyty',
    neverUsed: 'Nigdy',
    created: 'Utworzono',
    status: 'Status',
    active: 'Aktywna',
    inactive: 'Nieaktywna',
    tokenPrefix: 'Token',
    createTitle: 'Nowa aplikacja API',
    editTitle: 'Edytuj aplikację',
    nameRequired: 'Nazwa jest wymagana',
    scopesRequired: 'Wybierz co najmniej jedno uprawnienie',
    createSuccess: 'Aplikacja utworzona',
    updateSuccess: 'Zapisano zmiany',
    deleteSuccess: 'Aplikacja usunięta',
    regenerate: 'Wygeneruj nowy token',
    regenerateSuccess: 'Token wygenerowany ponownie',
    regenerateConfirmTitle: 'Wygenerować nowy token dla „{name}”?',
    regenerateConfirmMessage:
      'Dotychczasowy token natychmiast przestanie działać. Nowy token zobaczysz tylko raz.',
    tokenRevealTitle: 'Token aplikacji',
    tokenRevealDescription: 'Skopiuj token teraz — nie będzie już nigdy widoczny.',
    tokenRevealWarning:
      'Token jest wyświetlany tylko raz. Po zamknięciu tego okna można go wyłącznie wygenerować ponownie.',
    copyToken: 'Kopiuj',
    tokenCopied: 'Token skopiowany',
    deleteConfirmTitle: 'Usunąć aplikację „{name}”?',
    deleteConfirmMessage: 'Token natychmiast przestanie działać. Tej operacji nie można cofnąć.',
    delete: 'Usuń',
    close: 'Zamknij',
    empty: 'Brak aplikacji API',
  },

  users: {
    title: 'Użytkownicy',
    subtitle: 'Zarządzaj kontami i uprawnieniami użytkowników',
    addUser: 'Dodaj użytkownika',
    syncDirectory: 'Synchronizuj z Entra',
    syncDirectorySuccess: 'Zsynchronizowano {updated} z {scanned} użytkowników, przejrzano {members} osób z katalogu, utworzono {employees} rekordów pracowników',
    syncDirectoryStale: '{stale} pracowników z katalogu nie ma już w Entra — sprawdź ich w module HR',
    syncDirectoryUnavailable: 'Katalog Entra niedostępny — sprawdź uprawnienia aplikacji (User.Read.All)',
    syncDirectoryRunning: 'Synchronizacja już trwa',
    syncDirectoryFailed: 'Nie udało się zsynchronizować katalogu Entra',
    noUsers: 'Nie znaleziono użytkowników',
    noRoles: 'Brak ról',
    permissions: 'Uprawnienia',
    columns: {
      email: 'E-mail',
      name: 'Nazwa',
      source: 'Źródło',
      roles: 'Role',
      status: 'Status',
    },
    source: {
      entra: 'Microsoft',
      local: 'Lokalne',
    },
    status: {
      active: 'Aktywny',
      inactive: 'Nieaktywny',
    },
    roleNames: {
      ksiegowy: 'Księgowy',
      admin: 'Administrator',
      user: 'Użytkownik',
      pracownik: 'Pracownik',
      hr: 'HR',
      crm: 'CRM (handlowiec)',
    },
    moduleNames: {
      dashboard: 'Panel',
      expense: 'FV Koszty',
      sales: 'Sprzedaż',
      projects: 'Projekty (statystyki)',
      contractors: 'Kontrahenci',
      magazyn: 'Magazyn',
      mileage: 'Ewidencja przebiegu',
      employees: 'Pracownicy',
      tags: 'Tagi',
      hr: 'HR',
      fiscal: 'Fiskalizacja',
      crm: 'CRM',
    },
    form: {
      email: 'E-mail',
      displayName: 'Nazwa wyświetlana',
      company: 'Firma',
      role: 'Rola',
      modules: 'Moduły',
      tagsForModule: 'Ogranicz widoczność do tagów ({module})',
      sendInvite: 'Wyślij zaproszenie e-mailem',
      selectCompany: 'Wybierz firmę',
      selectRole: 'Wybierz rolę',
      noTagsForModule: 'Brak tagów zdefiniowanych dla tego modułu',
      accountType: 'Typ konta',
      providerEntra: 'Microsoft (Entra ID)',
      providerLocal: 'Konto lokalne (hasło)',
      entraHint:
        'Użytkownik loguje się kontem Microsoft — wybierz go z katalogu Entra. Uprawnienia zadziałają automatycznie przy pierwszym logowaniu, bez hasła i zaproszenia.',
      sendEntraNotice: 'Wyślij e-mail z informacją o dostępie',
      directoryUser: 'Osoba z katalogu Entra',
      searchDirectory: 'Wybierz osobę z katalogu…',
      searchDirectoryPlaceholder: 'Szukaj: imię i nazwisko lub e-mail…',
      searchDirectoryHint: 'Wpisz co najmniej 2 znaki',
      directoryNoResults: 'Brak wyników w katalogu',
      directoryUnavailable: 'Katalog Entra niedostępny — wpisz adres e-mail ręcznie',
      manager: 'Przełożony',
      managerLoading: 'Pobieranie danych przełożonego…',
      noManager: 'brak przełożonego',
    },
    directory: {
      title: 'Dane z katalogu Entra',
      jobTitle: 'Stanowisko',
      department: 'Dział',
      manager: 'Przełożony',
      noManager: 'brak przełożonego',
      syncedAt: 'Ostatnia synchronizacja',
      neverSynced: 'jeszcze nie synchronizowano',
    },
    createSuccess: 'Użytkownik utworzony',
    createdNoInvite: 'Użytkownik utworzony. Przekaż mu tymczasowe hasło poniżej.',
    temporaryPassword: 'Tymczasowe hasło',
    copyPassword: 'Kopiuj',
    passwordCopied: 'Skopiowano hasło',
    close: 'Zamknij',
    drawer: {
      title: 'Uprawnienia użytkownika',
      active: 'Aktywny',
      activeHint: 'Nieaktywny użytkownik nie może się zalogować',
      rolesInCompany: 'Role',
      addRole: 'Dodaj rolę',
      grants: 'Dostęp do modułów',
      roleAdded: 'Rola dodana',
      roleRemoved: 'Rola usunięta',
      unassignedTitle: 'Bez firmy (pracownik)',
      addUnassigned: 'Nadaj rolę pracownika bez firmy',
      userUpdated: 'Użytkownik zaktualizowany',
      addCompany: 'Dodaj firmę',
      searchCompany: 'Szukaj firmy…',
      noCompanyResults: 'Nie znaleziono firmy',
      pickRole: 'Wybierz rolę',
      noAccess: 'Ten użytkownik nie ma jeszcze dostępu do żadnej firmy',
      removeAccess: 'Usuń dostęp',
      removeAccessConfirmTitle: 'Usunąć dostęp do firmy?',
      removeAccessConfirmMessage:
        'Użytkownik straci wszystkie role i dostęp do modułów w firmie {company}.',
      accessRemoved: 'Dostęp usunięty',
      fullAccess: 'Pełny dostęp do wszystkich modułów (wynika z roli)',
      hrOnlyAccess: 'Dostęp do modułu HR (wynika z roli)',
      grantsOnlyForUserRole: 'Moduły mają zastosowanie tylko dla roli „Użytkownik”',
    },
    errors: {
      createFailed: 'Nie udało się utworzyć użytkownika',
      addRoleFailed: 'Nie udało się dodać roli',
      removeRoleFailed: 'Nie udało się usunąć roli',
      saveGrantsFailed: 'Nie udało się zapisać uprawnień',
      removeAccessFailed: 'Nie udało się usunąć dostępu',
      updateFailed: 'Nie udało się zaktualizować użytkownika',
    },
  },

  notifications: {
    title: 'Powiadomienia',
    empty: 'Brak powiadomień',
    markAllRead: 'Oznacz wszystkie jako przeczytane',
    justNow: 'przed chwilą',
    minutesAgo: '{m} min temu',
    hoursAgo: '{h} godz. temu',
    earlyPaymentRequest: '{name} prosi o wcześniejszy przelew za FV {invoice} — proponowana data: {date}',
    absenceReported: '{name} zgłasza: {type} {from} – {to}',
    absenceRequested: '{name} złożył(a) wniosek: {type} {from} – {to}',
    fundsNotReceivedInvoice: '⚠ {name} zgłasza brak środków za FV {invoice}',
    fundsNotReceived: '⚠ {name} zgłasza brak wypłaty',
    reimbursementRequested: '{name} prosi o zwrot {amount} PLN za {invoice}',
    reimbursementApproved: 'Twój zwrot {amount} PLN za {invoice} został zaakceptowany',
    reimbursementRejected: 'Twój zwrot za {invoice} został odrzucony: {reason}',
    myDataLocked: '🔒 {name}: zablokowano odsłanianie danych po zbyt wielu próbach weryfikacji PESEL — możliwe przejęcie sesji',
    b2bAccountNotOnWhitelist: 'Weryfikacja B2B: {contractor} — konto z faktury {invoice} NIE figuruje na Białej Liście VAT',
    b2bVatStatusNegative: 'Weryfikacja B2B: {contractor} ma negatywny status VAT w rejestrze',
    b2bVerificationUnavailable: 'Weryfikacja B2B: nie udało się zweryfikować {contractor} (rządowe API niedostępne)',
    documentPublished: 'Nowy dokument do zapoznania: {title}',
    documentAcceptanceReminder: 'Dokument {title} czeka na akceptację (termin {deadline})',
    documentAcceptanceReminderNoDeadline: 'Dokument {title} czeka na akceptację',
    documentOverdue: '{employeeName} nie zaakceptował(a) dokumentu {title} w terminie',
    documentReturned: 'Wrócił podpisany dokument: {title} ({file}) — do potwierdzenia',
    documentReturnedSignedBy:
      'Wrócił podpisany dokument: {title} ({file}) — podpisał(a) {name}, do potwierdzenia',
    documentReturnSuperseded:
      'Nowsza wersja podpisanego dokumentu: {title} ({file}) — zapisana w poczekalni, plik NIE został podmieniony automatycznie',
    documentReturnSupersededSignedBy:
      'Nowsza wersja podpisanego dokumentu: {title} ({file}) — dodatkowy podpis: {name}; zapisana w poczekalni, do decyzji operatora',
    documentReturnNeedsReview:
      'Zwrot do zamkniętego dokumentu {title} ({file}) wymaga sprawdzenia — nic nie zmieniono',
    documentReturnFailed:
      'Nie udało się zapisać zwróconego dokumentu {title} ({file}) — plik trzeba wysłać ponownie',
    documentCountersignPending:
      'Dokument „{title}” ({number}) czeka na podpis reprezentanta: {representativeName}',
    documentFullySigned:
      'Umowa „{title}” ({number}) — {employeeName} — została podpisana przez wszystkie strony',
    fiscalInvoiceRejected: 'Faktura fiskalna {number} została odrzucona przez KSeF',
    fiscalInvoiceRejectedWithError: 'Faktura fiskalna {number} została odrzucona przez KSeF: {error}',
    fiscalReceiptRejected: 'Paragon fiskalny na drukarce {printer} został odrzucony',
    fiscalReceiptRejectedWithError: 'Paragon fiskalny na drukarce {printer} został odrzucony: {error}',
    fiscalPrinterOffline: 'Drukarka fiskalna {printer} jest offline',
    fiscalDailyReportFailed: 'Raport dobowy drukarki {printer} ({date}) zakończył się niepowodzeniem',
    fiscalDailyReportFailedWithError:
      'Raport dobowy drukarki {printer} ({date}) zakończył się niepowodzeniem: {error}',
    fiscalDailyReportOverdue:
      'Brak raportu dobowego za {date} — drukarka {printer}. Sprawdź drukarkę/agenta fiskalnego.',
    crmLeadReceived: 'Nowy lead: {name} (źródło: {source})',
    crmLeadReceivedCompany: 'Nowy lead: {name}, {company} (źródło: {source})',
    unknownContractor: 'nieznany kontrahent',
    contractorDocumentExpiringOne: 'Dokument „{label}" ({contractor}) wygasa za 1 dzień',
    contractorDocumentExpiringSoon: 'Dokument „{label}" ({contractor}) wygasa za {days} dni',
    contractorDocumentExpiringToday: 'Dokument „{label}" ({contractor}) wygasa dziś',
    contractorDocumentExpiredOne: 'Dokument „{label}" ({contractor}) wygasł 1 dzień temu',
    contractorDocumentExpired: 'Dokument „{label}" ({contractor}) wygasł {days} dni temu',
  },
  hr: {
    title: 'HR',
    tabs: {
      me: 'Mój urlop',
      team: 'Zespół',
      admin: 'Kadry',
      invoices: 'Moje faktury',
      reimbursements: 'Zwroty',
      myData: 'Moje dane',
      documents: 'Dokumenty',
    },
    myData: {
      revealLockWarning: 'Po 5 błędnych próbach dostęp do Twoich danych wrażliwych zostanie zablokowany, a HR otrzyma powiadomienie — odblokowanie będzie wymagało HR lub administratora.',
      title: 'Moje dane',
      readOnlyHint: 'Dane może edytować tylko HR — zgłoś zmiany swojemu opiekunowi kadrowemu.',
      sectionPersonal: 'Dane osobowe',
      sectionContact: 'Kontakt',
      sectionAddress: 'Adres',
      sectionEmployment: 'Zatrudnienie',
      sectionSensitive: 'Dokumenty i konto',
      sectionPayroll: 'Wynagrodzenie',
      firstName: 'Imię',
      lastName: 'Nazwisko',
      dateOfBirth: 'Data urodzenia',
      bornOn: 'ur. {date}',
      sex: 'Płeć',
      sexMale: 'Mężczyzna',
      sexFemale: 'Kobieta',
      privateEmail: 'E-mail prywatny',
      privatePhone: 'Telefon',
      workEmail: 'E-mail służbowy',
      street: 'Ulica',
      postalCode: 'Kod pocztowy',
      city: 'Miejscowość',
      country: 'Kraj',
      employmentType: 'Typ zatrudnienia',
      contractStart: 'Początek umowy',
      contractExpiry: 'Koniec umowy',
      jobTitle: 'Stanowisko',
      department: 'Dział',
      manager: 'Przełożony',
      fte: 'Etat',
      dailyNorm: 'Norma dzienna',
      taxOffice: 'Urząd skarbowy',
      nfzBranch: 'Oddział NFZ',
      pesel: 'PESEL',
      idDocument: 'Dokument tożsamości',
      idDowod: 'Dowód osobisty',
      idPassport: 'Paszport',
      iban: 'Konto bankowe',
      reveal: 'Odsłoń dane wrażliwe',
      revealTitle: 'Potwierdź tożsamość',
      revealHint: 'Podaj ostatnie 4 cyfry swojego numeru PESEL, aby odsłonić dane wrażliwe na 5 minut.',
      revealInput: 'Ostatnie 4 cyfry PESEL',
      revealSubmit: 'Odsłoń',
      revealNoPesel: 'Brak numeru PESEL w kartotece — skontaktuj się z HR, aby odsłonić dane.',
      revealLocked: 'Odsłanianie zablokowane po zbyt wielu nieudanych próbach — poproś HR lub admina o odblokowanie.',
      hideNow: 'Ukryj teraz',
      revealedFor: 'Dane odsłonięte — ukryją się automatycznie za {s} s',
      payrollGross: 'Brutto',
      payrollZus: 'Składki ZUS (pracownik)',
      payrollZusShort: 'ZUS',
      payrollHealth: 'Składka zdrowotna',
      payrollHealthShort: 'Zdrowotna',
      payrollPit: 'Zaliczka PIT',
      payrollPitShort: 'PIT',
      payrollPpk: 'PPK (pracownik)',
      payrollPpkShort: 'PPK',
      payrollNet: 'Netto',
      payrollEmployerCost: 'Łączny koszt pracodawcy',
      payrollRate: 'Stawka',
      payrollUnavailable: 'Brak danych do wyliczenia wynagrodzenia (brak stawki lub typu umowy).',
      perMonth: '/mc',
      perHour: '/h',
    },
    myInvoices: {
      title: 'Moje faktury',
      empty: 'Brak faktur powiązanych z Twoją firmą',
      colNumber: 'Numer FV',
      colReceived: 'Otrzymana',
      colDue: 'Termin płatności',
      colAmount: 'Kwota',
      colStatus: 'Status',
      status: {
        received: 'Otrzymana',
        passed_for_payment: 'Przekazana do płatności',
        paid: 'Zapłacona',
      },
      termInfo: 'Termin płatności: 14 dni od daty otrzymania faktury (zgodnie z umową).',
      earlyPayment: 'Poproś o wcześniejszy przelew',
      earlyPaymentTitle: 'Wcześniejszy przelew — FV {invoice}',
      earlyPaymentDate: 'Proponowana data przelewu',
      earlyPaymentNote: 'Komentarz (opcjonalnie)',
      earlyPaymentSend: 'Wyślij prośbę',
      earlyPaymentSent: 'Prośba wysłana — admini i przełożony zostali powiadomieni',
      fundsAlarm: 'Nie otrzymałem środków',
      fundsAlarmConfirm: 'Zgłosić brak środków za FV {invoice}? Admini firmy i Twój przełożony dostaną alarm.',
      fundsAlarmSent: 'Alarm wysłany',
    },
    payAlarm: {
      button: 'Nie otrzymałem wynagrodzenia',
      confirm: 'Zgłosić brak wypłaty? Admini firmy i Twój przełożony dostaną alarm.',
      sent: 'Alarm wysłany',
    },
    reimbursements: {
      approveConfirmTitle: 'Zatwierdzić zwrot?',
      approveConfirmBody: 'Spowoduje to zatwierdzenie wypłaty {amount} dla {employee} i oznaczenie faktury jako opłaconej. Tej operacji nie można cofnąć.',
      title: 'Zwroty wydatków',
      request: 'Zażądaj zwrotu za FV',
      empty: 'Brak wniosków o zwrot',
      approvalsTitle: 'Do akceptacji',
      myTitle: 'Moje wnioski',
      colInvoice: 'FV / Paragon',
      colVendor: 'Wystawca',
      colEmployee: 'Pracownik',
      colAmount: 'Kwota zwrotu',
      colStatus: 'Status',
      colRequested: 'Złożono',
      status: {
        pending_approval: 'Czeka na akceptację',
        approved: 'Zaakceptowany — do przelewu',
        rejected: 'Odrzucony',
        exported: 'Przelew zlecony',
      },
      searchTitle: 'Znajdź swoją fakturę / paragon',
      searchHintNip: 'Podaj NIP wystawcy i kwotę brutto z dokumentu (ścieżka dla polskich FV).',
      searchNip: 'NIP wystawcy',
      searchGross: 'Kwota brutto',
      searchByDate: 'Nie znaleziono? Szukaj po dacie wystawienia',
      searchIssueDate: 'Data wystawienia',
      searchHintDate: 'Dla dokumentów zagranicznych (np. w EUR): data wystawienia + kwota brutto, bez NIP.',
      searchButton: 'Szukaj',
      searchEmpty: 'Nie znaleziono pasującego dokumentu. Jeśli to skan — wyślij go najpierw na periscope@fssrke3.com (lub wgraj w FV Koszty) i spróbuj ponownie za kilka minut.',
      confirmTitle: 'Potwierdź wniosek o zwrot',
      reimbursedAmount: 'Kwota zwrotu (PLN)',
      fxNote: 'Dokument w walucie obcej — kwota przeliczona kursem NBP z dnia poprzedzającego wystawienie.',
      ibanLabel: 'Przelew na konto',
      submit: 'Wyślij wniosek',
      submitted: 'Wniosek wysłany — przełożony i admini zostali powiadomieni',
      approve: 'Akceptuj',
      reject: 'Odrzuć',
      rejectTitle: 'Odrzuć wniosek',
      rejectReason: 'Powód odrzucenia',
      approved: 'Wniosek zaakceptowany',
      rejected: 'Wniosek odrzucony',
      rejectedReason: 'Powód: {reason}',
    },
    notLinked: {
      title: 'Brak powiązanej kartoteki pracownika',
      body: 'Twoje konto nie jest powiązane z żadną kartoteką pracownika w tej firmie, więc nie ma salda urlopowego, kalendarza ani wniosków do wyświetlenia.',
      action: 'Jeśli pracujesz w tej firmie, poproś dział kadr lub administratora o sprawdzenie Twojej kartoteki — potrzebny jest w niej Twój służbowy adres e-mail. Powiązanie tworzy się potem automatycznie (przy kolejnym logowaniu lub po nocnej synchronizacji katalogu).',
      adminHint: 'Jako HR/administrator: w zakładce Kadry sprawdź, czy Twoja kartoteka ma poprawny służbowy e-mail, a następnie uruchom Ustawienia → Użytkownicy → Synchronizuj katalog. Konta łączą się z kartotekami po identyfikatorze Entra (OID); adres e-mail to tylko dopasowanie zapasowe dla kartotek wprowadzonych ręcznie.',
    },
    types: {
      vacation: 'Urlop wypoczynkowy',
      vacation_on_demand: 'Urlop na żądanie',
      unpaid: 'Urlop bezpłatny',
      occasional: 'Urlop okolicznościowy',
      childcare_art188: 'Opieka nad dzieckiem (art. 188)',
      care_art173: 'Urlop opiekuńczy',
      force_majeure_art148: 'Siła wyższa',
      sick: 'Zwolnienie lekarskie',
      unavailability_b2b: 'Niedostępność',
      remote_work: 'Praca zdalna',
    },
    typesShort: {
      vacation: 'Urlop',
      vacation_on_demand: 'Na żądanie',
      unpaid: 'Bezpłatny',
      occasional: 'Okolicznośc.',
      childcare_art188: 'Opieka',
      care_art173: 'Opiekuńczy',
      force_majeure_art148: 'Siła wyższa',
      sick: 'L4',
      unavailability_b2b: 'Niedostępny',
      remote_work: 'Zdalna',
    },
    status: {
      pending: 'Oczekuje',
      approved: 'Zaakceptowany',
      rejected: 'Odrzucony',
      cancelled: 'Anulowany',
    },
    balance: {
      remainingLabel: 'Urlop do wykorzystania w {year}',
      used: 'Wykorzystane',
      pending: 'Oczekujące',
      remaining: 'Pozostałe',
      entitlement: 'Wymiar',
      entitlementValue: '{base} + {extra}',
      onDemand: 'Urlop na żądanie',
      onDemandOf: '{used}/{cap} dni',
      sickDaysYtd: 'Chorobowe w tym roku',
      overdue: 'Urlop zaległy',
      overdueWarning: '{amount} zaległego — wykorzystaj do {deadline} (art. 168 KP)',
      noOverdue: 'Brak zaległego urlopu',
      request: 'Złóż wniosek',
      b2bTitle: 'Współpraca B2B / zlecenie',
      b2bBody: 'Urlop wypoczynkowy nie dotyczy Twojej formy współpracy. Zamiast tego zgłaszasz niedostępność — przełożony dostaje powiadomienie automatycznie, bez akceptacji.',
      noContractType: 'HR nie skonfigurował jeszcze typu Twojej umowy. Do tego czasu możesz zgłosić niedostępność lub pracę zdalną.',
      unavailabilityYtd: 'Niedostępność w tym roku',
      remoteYtd: 'Dni zdalne w tym roku',
      reportUnavailability: 'Zgłoś niedostępność',
    },
    calendar: {
      hint: 'Przeciągnij po dniach, aby złożyć wniosek — weekendy i święta nigdy nie pomniejszają puli.',
      today: 'Dziś',
      prevMonth: 'Poprzedni miesiąc',
      nextMonth: 'Następny miesiąc',
      legendWeekend: 'Weekend / święto',
      legendPending: 'oczekujący',
    },
    request: {
      title: 'Nowy wniosek urlopowy',
      titleB2b: 'Zgłoszenie niedostępności',
      type: 'Rodzaj nieobecności',
      groupPool: 'Pula urlopowa',
      groupOther: 'Inne nieobecności',
      groupInfo: 'Powiadomienia',
      dateFrom: 'Od',
      dateTo: 'Do',
      halfDay: 'Pół dnia ({hours} h)',
      halfDayStart: 'Pół dnia — pierwszy dzień',
      halfDayEnd: 'Pół dnia — ostatni dzień',
      reason: 'Powód (opcjonalnie)',
      reasonOccasionalHint: 'np. ślub, narodziny dziecka, pogrzeb',
      workingDays: 'Dni robocze',
      workingDaysValue: '{days} d ({hours} h)',
      freeDaysExcluded: 'Dni wolne w zakresie ({count}) nie pomniejszają puli',
      afterApproval: 'Po zatwierdzeniu zostanie',
      onDemandCounter: 'Na żądanie w tym roku',
      overlapWarning: 'Pokrywa się z Twoim wnioskiem ({range})',
      errors: {
        crossYear: 'Wniosek musi mieścić się w jednym roku kalendarzowym — podziel go na 31 grudnia.',
        insufficient: 'Wniosek przekracza dostępny urlop o {amount}.',
        onDemandCap: 'Urlop na żądanie to maksymalnie 4 dni w roku (art. 167² KP).',
        noWorkingDays: 'Wybrany zakres zawiera tylko weekendy/święta.',
      },
      hints: {
        occasional: 'Do 2 dni roboczych na wydarzenie rodzinne — ślub, narodziny, pogrzeb (§15 rozp. MPiPS).',
        childcare_art188: '2 dni w roku kalendarzowym, pełnopłatne (art. 188 KP).',
        care_art173: 'Do 5 dni bezpłatnych rocznie na opiekę nad członkiem rodziny (art. 173¹ KP).',
        force_majeure_art148: '2 dni w roku, płatne 50%, na pilne sprawy rodzinne (art. 148¹ KP).',
        unpaid: 'Nie pomniejsza puli urlopowej (art. 174 KP).',
        remote_work: 'Tylko informacyjnie — widoczne w kalendarzu, bez akceptacji.',
        unavailability_b2b: 'Tylko powiadomienie — przełożony zostaje poinformowany, bez akceptacji.',
      },
      submit: 'Złóż wniosek',
      submitInfo: 'Zapisz zgłoszenie',
      cancel: 'Anuluj',
      successTitle: 'Wniosek złożony',
      successInfoTitle: 'Zgłoszenie zapisane',
      successBalance: 'Po zatwierdzeniu zostanie Ci {amount}',
    },
    myRequests: {
      title: 'Moje wnioski',
      empty: 'Nie masz jeszcze żadnych wniosków — zaznacz dni w kalendarzu, aby złożyć wniosek.',
      showAll: 'Pokaż wszystkie ({count})',
      showLess: 'Pokaż mniej',
      cancel: 'Anuluj wniosek',
      cancelTitle: 'Anulować ten wniosek?',
      cancelBody: '{type}, {range}. Tej operacji nie można cofnąć.',
      cancelConfirm: 'Anuluj wniosek',
      cancelKeep: 'Zostaw',
      cancelled: 'Wniosek anulowany',
      decisionNote: 'Komentarz: {note}',
    },
    whosOut: {
      title: 'Kto jest nieobecny — najbliższe 7 dni',
      empty: 'Nikt z Twojego zespołu nie planuje nieobecności w najbliższym tygodniu.',
      pendingSuffix: '(oczekuje)',
    },
    team: {
      queueTitle: 'Wnioski do akceptacji',
      queueEmpty: 'Brak oczekujących wniosków — wszystko zatwierdzone.',
      afterApproval: 'Po zatwierdzeniu zostanie: {amount}',
      conflict: '{count} z {total} osób w zespole nieobecne w tym terminie: {names}',
      approve: 'Zatwierdź',
      reject: 'Odrzuć',
      note: 'Dodaj komentarz',
      notePlaceholder: 'Opcjonalny komentarz dla pracownika…',
      approved: 'Wniosek zatwierdzony',
      rejected: 'Wniosek odrzucony',
      actionFailed: 'Operacja nie powiodła się',
      calendarTitle: 'Kalendarz zespołu',
      workingDaysShort: '{days} rob.',
    },
    grid: {
      month: 'Miesiąc',
      quarter: 'Kwartał',
      department: 'Dział',
      allDepartments: 'Wszystkie działy',
      type: 'Rodzaj',
      allTypes: 'Wszystkie rodzaje',
      allStatuses: 'Oczekujące + zaakceptowane',
      approvedOnly: 'Tylko zaakceptowane',
      empty: 'Brak nieobecności w tym okresie.',
      showMore: 'Pokaż kolejne {count}',
      people: '{count} os.',
    },
    admin: {
      tabs: {
        calendar: 'Kalendarz firmowy',
        register: 'Ewidencja urlopowa',
        sick: 'Chorobowe (e-ZLA)',
        stats: 'Statystyki',
        payroll: 'Kalkulator wynagrodzeń',
        compliance: 'Terminy i zgodność',
      },
    },
    compliance: {
      title: 'Moje dokumenty i terminy',
      queueTitle: 'Dokumenty do weryfikacji',
      adminTitle: 'Terminy i zgodność',
      types: {
        student_card: 'Legitymacja studencka',
        medical_exam: 'Badania lekarskie',
        bhp_training: 'Szkolenie BHP',
        contract_expiry: 'Koniec umowy',
        rud_report: 'Zgłoszenie umowy o dzieło do ZUS (RUD)',
      },
      status: {
        missing: 'Brak',
        pending_verification: 'Do weryfikacji',
        valid: 'Ważne',
        expired: 'Wygasło',
        rejected: 'Odrzucone',
      },
      validUntil: 'Ważne do {date}',
      contractUntil: 'Umowa do {date}',
      daysLeft: 'zostało {days} dni',
      daysOverdue: '{days} dni po terminie',
      dueToday: 'ostatni dzień ważności',
      rejectedReason: 'Powód: {reason}',
      submittedAt: 'Wysłano {date}',
      upload: 'Dodaj dokument',
      uploadAgain: 'Dodaj ponownie',
      uploadTitle: 'Dodaj: {type}',
      uploadHint: 'PDF lub zdjęcie (JPG/PNG/WebP/HEIC), maks. 10 MB. Dokument trafi do weryfikacji przez przełożonego.',
      employerManagedHint: 'Organizuje pracodawca',
      recordDocument: 'Dodaj dokument',
      recordHint: 'PDF lub zdjęcie (JPG/PNG/WebP/HEIC), maks. 10 MB. Dokument trafi do kartoteki pracownika i od razu będzie ważny — bez weryfikacji.',
      recordSubmit: 'Zapisz dokument',
      recordSuccess: 'Dokument zapisany w kartotece pracownika',
      uploadFile: 'Plik',
      uploadValidUntil: 'Dokument ważny do',
      uploadSubmit: 'Wyślij do weryfikacji',
      uploadSuccess: 'Dokument wysłany do weryfikacji',
      empty: 'Brak wymaganych dokumentów dla Twojej formy zatrudnienia.',
      queueEmpty: 'Brak dokumentów oczekujących na weryfikację.',
      preview: 'Podgląd',
      verify: 'Zweryfikuj',
      verified: 'Dokument zweryfikowany',
      rejectAction: 'Odrzuć',
      rejectTitle: 'Odrzuć dokument',
      rejectBody: '{type} — {employee}. Pracownik otrzyma e-mail z powodem odrzucenia.',
      rejectReasonLabel: 'Powód odrzucenia',
      rejectReasonPlaceholder: 'np. nieczytelny skan, brak daty ważności',
      rejectConfirm: 'Odrzuć dokument',
      rejectedToast: 'Dokument odrzucony',
      actionFailed: 'Operacja nie powiodła się',
      filterType: 'Typ',
      filterStatus: 'Status',
      filterDepartment: 'Dział',
      filterAll: 'Wszystkie',
      exportCsv: 'Eksport CSV',
      colEmployee: 'Pracownik',
      colDepartment: 'Dział',
      colType: 'Wymóg',
      colStatus: 'Status',
      colValidUntil: 'Ważne do',
      colDaysLeft: 'Dni',
      colDocument: 'Dokument',
      rudMarkDone: 'Oznacz jako zgłoszone',
      rudMarkedDone: 'Zgłoszenie RUD oznaczone jako wykonane',
    },
    payroll: {
      title: 'Kalkulator wynagrodzeń (2026)',
      subtitle: 'Brutto ↔ netto i całkowity koszt pracodawcy wg polskich przepisów',
      form: 'Forma zatrudnienia',
      forms: {
        uop: 'Umowa o pracę',
        zlecenie: 'Umowa zlecenie',
        zlecenie_student: 'Zlecenie — student/uczeń do 26 lat',
        b2b: 'B2B (faktura)',
        powolanie: 'Członek zarządu (powołanie)',
        dzielo: 'Umowa o dzieło',
      },
      modeGross: 'Od brutto',
      modeNet: 'Od netto',
      amountGross: 'Kwota brutto (zł/mc)',
      amountNet: 'Kwota netto — na rękę (zł/mc)',
      options: 'Opcje',
      pit2: 'PIT-2 (kwota zmniejszająca 300 zł/mc)',
      ppk: 'PPK (pracownik 2%, pracodawca 1,5%)',
      kup300: 'Podwyższone KUP 300 zł (dojazd spoza miejscowości)',
      under26: 'Osoba poniżej 26 lat (ulga dla młodych)',
      sickness: 'Dobrowolna składka chorobowa',
      kupAutorskie: 'KUP 50% (prawa autorskie)',
      kupAutorskieDzielo: 'Przeniesienie praw autorskich (KUP 50%)',
      ownEmployer: 'Dzieło z własnym pracodawcą (pełny ZUS jak przy umowie o pracę)',
      emptyState: 'Podaj kwotę, aby zobaczyć rozliczenie',
      invalidAmount: 'Podaj poprawną kwotę',
      calcFailed: 'Nie udało się obliczyć',
      resultGross: 'Brutto',
      resultNet: 'Netto (na rękę)',
      resultEmployerCost: 'Koszt pracodawcy',
      b2bNote: 'B2B: koszt firmy to kwota netto faktury — składki i podatek rozlicza wykonawca.',
      dzieloNote: 'Umowa o dzieło: brak składek ZUS i zdrowotnej — koszt firmy = brutto. Pamiętaj o zgłoszeniu RUD do ZUS w ciągu 7 dni od zawarcia (nie dotyczy dzieła z własnym pracownikiem).',
    },
    register: {
      year: 'Rok',
      employee: 'Pracownik',
      contract: 'Umowa',
      fte: 'Etat',
      seniority: 'Staż',
      seniorityHelp: 'Staż urlopowy sprzed zatrudnienia: poprzedni pracodawcy plus najkorzystniejszy okres nauki (zasadnicza zawodowa 3 l., liceum 4 l., technikum 5 l., policealna 6 l., studia 8 l. — art. 155 KP; okresy nauki nie sumują się). Lata pracy w tej firmie system dolicza automatycznie z daty początku umowy. 20 dni poniżej 10 lat łącznego stażu, 26 dni od 10 lat.',
      calc: {
        open: 'Kalkulator stażu (art. 155 KP)',
        title: 'Kalkulator stażu urlopowego',
        education: 'Ukończona szkoła (najwyższa)',
        employment: 'Lata u poprzednich pracodawców (poza okresem nauki)',
        rules: 'Okresy nauki nie sumują się — liczy się jedna, najkorzystniejsza szkoła (art. 155 §1 KP). Nauka w trakcie zatrudnienia: liczy się korzystniejszy z okresów, nie oba (art. 155 §2 KP). Lat pracy w obecnej firmie NIE wpisuj — system dolicza je automatycznie z daty początku umowy.',
        suggestion: 'Sugerowany staż',
        years: 'lat',
        daysSuffix: 'dni urlopu',
        apply: 'Zastosuj',
        edu: {
          none: 'Brak / nie dotyczy',
          zawodowa: 'Zasadnicza szkoła zawodowa',
          liceum: 'Liceum ogólnokształcące',
          technikum: 'Średnia zawodowa (technikum)',
          policealna: 'Szkoła policealna',
          wyzsza: 'Szkoła wyższa (lic./inż./mgr)',
        },
      },
      entitlement: 'Wymiar',
      carryOver: 'Zaległy z ub. roku',
      adjustment: 'Korekta',
      used: 'Wykorzystano',
      pendingCol: 'Oczekuje',
      remaining: 'Pozostało',
      overdue: 'Zaległy',
      overdueBy: 'Wykorzystać do {deadline} (art. 168 KP)',
      firstJobShort: '1. praca',
      notConfigured: 'nie ustawiono',
      noPool: 'Brak urlopu ustawowego',
      uopOnly: 'Edycja tylko dla umów o pracę (UOP)',
      edit: 'Edytuj',
      save: 'Zapisz',
      cancel: 'Anuluj',
      saved: 'Zapisano konfigurację urlopową',
      exportCsv: 'Eksport CSV',
      empty: 'Brak pracowników.',
    },
    sick: {
      title: 'Rejestr zwolnień lekarskich',
      add: 'Dodaj e-ZLA',
      import: 'Import z PUE',
      employee: 'Pracownik',
      employeePlaceholder: 'Wybierz pracownika…',
      period: 'Okres',
      days: 'Dni',
      code: 'Kod',
      codeNone: 'brak',
      codeHint: {
        A: 'ta sama choroba do 60 dni',
        B: 'ciąża',
        C: 'nadużycie alkoholu',
        D: 'gruźlica',
        E: 'choroba zakaźna',
      },
      careForCol: 'Opieka',
      careSelf: 'własna choroba',
      careChild: 'dziecko',
      careFamily: 'członek rodziny',
      series: 'Nr serii',
      source: 'Źródło',
      sourceManual: 'ręcznie',
      sourcePue: 'import PUE',
      optional: 'opcjonalnie',
      empty: 'Brak zwolnień w tym roku — wprowadź e-ZLA ręcznie lub zaimportuj raport z PUE.',
      dialogTitle: 'Wprowadź zaświadczenie e-ZLA',
      dialogBody: 'ZUS nie powiadamia pracodawców — wprowadź zaświadczenie dokładnie tak, jak zostało wystawione. Zapisujemy wyłącznie kod literowy, nigdy diagnozę.',
      overrideLabel: 'Zastosuj art. 166 KP — przerwij pokrywający się urlop',
      save: 'Zapisz',
      created: 'Zwolnienie zarejestrowane',
      splitsInfo: 'Skorygowano wnioski urlopowe: {count} (art. 166 KP)',
    },
    pue: {
      title: 'Import raportu PUE / eZUS',
      step1: 'Wgraj raport e-ZLA wyeksportowany z PUE ZUS (CSV lub XLSX). Wiersze dopasowujemy po PESEL, potem po nazwisku i dacie urodzenia.',
      selectFile: 'Wybierz plik',
      parsing: 'Analizowanie pliku…',
      matched: 'Dopasowane',
      unmatched: 'Niedopasowane',
      conflicts: 'Konflikty',
      matchCol: 'Dopasowanie',
      matchPesel: 'dopasowano po PESEL',
      matchNameDob: 'dopasowano po nazwisku + dacie ur.',
      matchNone: 'brak pasującego pracownika',
      alreadyImported: 'już zaimportowane — pominięte',
      conflictNote: 'pokrywa się z zatwierdzonym urlopem — import zastosuje art. 166 KP',
      rowError: 'błąd wiersza: {error}',
      commit: 'Importuj wiersze: {count}',
      back: 'Zacznij od nowa',
      cancel: 'Anuluj',
      close: 'Zamknij',
      done: 'Import zakończony: zaimportowano {created}, pominięto {skipped}.',
    },
    stats: {
      absenceRate: 'Wskaźnik absencji (ten miesiąc)',
      ofWorkingTime: 'czasu pracy wg harmonogramu',
      sickDaysYtd: 'Dni chorobowe ({year})',
      pendingRequests: 'Oczekujące wnioski',
      outNext7: 'Nieobecni w najbliższych 7 dniach',
      people: '{count} os.',
      byType: 'Dni nieobecności wg rodzaju ({year})',
      exportCsv: 'Eksport CSV',
      empty: 'Brak danych o nieobecnościach.',
      yearLabel: 'Rok',
      monthlyChartTitle: 'Nieobecności miesiąc po miesiącu ({year})',
      topChartTitle: 'Top 10 pracowników wg dni nieobecności ({year})',
      chartVacation: 'Urlop wypoczynkowy',
      chartSick: 'Chorobowe (L4)',
      chartOther: 'Pozostałe',
      tableTitle: 'Statystyki per pracownik ({year})',
      colEmployee: 'Pracownik',
      colDepartment: 'Dział',
      colType: 'Typ umowy',
      colEntitlement: 'Przysługuje',
      colUsed: 'Wykorzystane',
      colPending: 'Oczekujące',
      colRemaining: 'Pozostało',
      colOnDemand: 'Na żądanie',
      colSick: 'L4',
      colUnavailability: 'Niedostępność',
      colRemote: 'Zdalne',
      colRate: '% absencji',
    },
    common: {
      loadFailed: 'Nie udało się wczytać danych HR',
    },
    documents: {
      tab: 'Dokumenty',
      title: 'Dokumenty',
      pendingTitle: 'Do akceptacji',
      doneTitle: 'Zaakceptowane',
      libraryTitle: 'Biblioteka dokumentów',
      emptyPending: 'Brak dokumentów oczekujących na akceptację.',
      emptyDone: 'Brak zaakceptowanych dokumentów.',
      emptyLibrary: 'Biblioteka dokumentów jest pusta.',
      deadline: 'Termin: {date}',
      publishedAt: 'Opublikowano {date}',
      accept: 'Akceptuję',
      download: 'Pobierz PDF',
      signInstruction:
        'Pobierz PDF, podpisz (odręcznie lub elektronicznie na gov.pl) i odeślij na periscope@fssrke3.com albo wgraj poniżej.',
      returnedNote: 'Przekazany — czeka na potwierdzenie HR',
      methods: {
        electronic: 'Elektronicznie',
        physical: 'Skan podpisany',
      },
      acceptTitle: 'Potwierdź akceptację',
      acceptHint: 'Podaj ostatnie 4 cyfry swojego numeru PESEL, aby zaakceptować dokument „{title}”.',
      acceptInput: 'Ostatnie 4 cyfry PESEL',
      acceptSubmit: 'Akceptuję',
      acceptNote: 'Akceptacja zostanie zapisana wraz z datą, wersją dokumentu i adresem IP.',
      acceptSuccess: 'Dokument zaakceptowany',
      acceptLocked: 'Akceptacja zablokowana po zbyt wielu nieudanych próbach — poproś HR o odblokowanie.',
      loadError: 'Nie udało się wczytać dokumentów',
      downloadError: 'Nie udało się pobrać dokumentu',
      acceptError: 'Nie udało się zaakceptować dokumentu',
      uploadSigned: 'Wgraj podpisany skan',
      uploadFile: 'Plik skanu',
      uploadSubmit: 'Wgraj',
      uploadSuccess: 'Podpisany dokument przekazany',
      uploadError: 'Nie udało się wgrać dokumentu',
    },
  },

  registry: {
    lookup: 'Rejestr',
    fetch: 'Pobierz z rejestru',
    lookupSuccess: 'Dane firmy pobrane z rejestru państwowego',
    lookupNotFound: 'Brak wpisu w rejestrze dla tego identyfikatora',
    lookupFailed: 'Rejestr nie odpowiada — spróbuj ponownie później',
    refresh: 'Odśwież z rejestru',
    refreshSuccess: 'Dane kontrahenta odświeżone z rejestru',
    refreshFailed: 'Odświeżenie z rejestru nie powiodło się',
    refreshNoId: 'Kontrahent musi mieć polski NIP albo unijny numer VAT',
    vatStatus: 'Status VAT',
    vatStatusActive: 'Czynny podatnik VAT',
    vatStatusExempt: 'Zwolniony z VAT',
    vatStatusNotRegistered: 'Niezarejestrowany do VAT',
    viesValid: 'VIES: numer VAT aktywny',
    viesInvalid: 'VIES: numer VAT nieaktywny',
    verifiedAt: 'Zweryfikowano {date}',
    neverVerified: 'Nigdy nie weryfikowano w rejestrze',
    accountsTitle: 'Konta bankowe z Białej Listy',
    accountsMatched: 'Zapisane konta potwierdzone na Białej Liście',
    accountsMissingLocally: 'Na Białej Liście, ale niezapisane u kontrahenta',
    accountsNotOnWhitelist: 'Zapisane konta NIEOBECNE na Białej Liście',
    accountsAllMatched: 'Wszystkie zapisane konta są na Białej Liście',
    whitelistWarning: 'To konto NIE figuruje na Białej Liście VAT!',
    whitelistWarningDetail: 'Zweryfikuj rachunek sprzedawcy przed płatnością — przelewy powyżej 15 000 zł na konto spoza listy grożą sankcjami podatkowymi.',
    whitelistUnverified: 'Biała Lista: niezweryfikowano',
    whitelistOk: 'Konto potwierdzone na Białej Liście VAT',
  },

  // Weryfikacja podpisów cyfrowych zwróconych skanów (PAdES)
  signatures: {
    kind: {
      qualified: 'Podpis kwalifikowany',
      trusted_profile: 'Podpis zaufany (podpis.gov.pl)',
      other: 'Podpis cyfrowy',
    },
    integrity: {
      valid: 'Integralność potwierdzona',
      invalid: 'Plik zmieniony po podpisaniu',
      unverifiable: 'Nie udało się zweryfikować',
    },
    partialCoverage: 'częściowe pokrycie podpisem',
    restCoveredByLater: 'dalsza część objęta późniejszym podpisem',
    seal: 'Pieczęć: {seal}',
    noSignature: 'brak podpisu cyfrowego (skan papierowy)',
    unknownSigner: 'nieznany podpisujący',
    issuer: 'Wystawca: {issuer}',
    signedAt: 'Podpisano: {date}',
    verifying: 'Weryfikacja podpisu…',
    retry: 'Ponów weryfikację',
    verify: 'Zweryfikuj podpis',
  },
};
